/**
 * GA4-01 — the single place that decides whether a GA4 hit may be sent, and
 * what URL it may carry.
 *
 * WHY THIS EXISTS. The GA4 tag is injected globally in `src/pages/+Head.tsx`,
 * so it loaded on every route — including `/auth/callback`, whose URL carries
 * the Supabase `token_hash` (see pages/AuthCallback.tsx:567-576), and
 * `/reset-password`, whose recovery `access_token` arrives in the URL fragment.
 * gtag's automatic `page_location` is `document.location.href` verbatim, so
 * every one of those loads handed Google a single-use auth credential. Admin
 * screens were measured for the same reason: the tag has no idea which route it
 * is on.
 *
 * WHAT IT DOES. Two primitives, used by every GA4 call site in the app:
 *
 *   1. `isAnalyticsBlockedPath` — a route-guard predicate. No hit of any kind
 *      (page_view or event) leaves a blocked route.
 *   2. `sanitizeAnalyticsUrl` — an allowlist URL scrubber. Every hit,
 *      site-wide, carries a `page_location` reduced to path + the six campaign
 *      params below, with the fragment always dropped.
 *
 * The guard is a per-hit check on the CURRENT path, never a latch: a visitor
 * who lands on `/auth/callback` and is then redirected to `/` is measured
 * normally from that redirect onwards. A session-wide kill switch would have
 * made every OAuth login invisible.
 *
 * GA4-13 — WHY THE GUARD WAS NOT ENOUGH. Everything above governs hits the app
 * SENDS. It cannot govern the hits gtag.js sends by itself: the moment
 * `gtag('config', ...)` runs, gtag.js attaches its own Enhanced Measurement DOM
 * listeners (scroll, outbound click, site search, video, file download, form
 * interactions) and those fire straight at Google's collection endpoint with
 * `dl = document.location.href` verbatim — no `sendGaEvent`, no sanitizer, no
 * route check. Production verification on 2026-08-23 caught exactly that: a
 * `scroll` hit (`en=scroll`, `epn.percent_scrolled=90`) left
 * `/auth/callback?token_hash=…&type=signup` carrying the token in `dl`.
 * Enhanced Measurement is a stream-level GA4 console setting; it cannot be
 * switched off per page load. So the only fix is to never let the tag boot on a
 * blocked route at all — `src/pages/+Head.tsx` no longer renders the bootstrap
 * there, and `ensureGtagBootstrapped()` below boots it on the client instead,
 * the first time a hit is due on an allowed route.
 *
 * GA4-02 — THE SECOND FACTOR. Everything above is about WHERE the tag may
 * boot. It says nothing about WHETHER the visitor agreed to be measured at all,
 * and until this item the answer was "we never asked": the tag booted on the
 * first paint of every allowed route, for everyone. Consent is now the other
 * half of every gate in this file, and it is a LOAD-TIME gate rather than a
 * parameter on a hit — the locked decision is Basic Consent Mode, so gtag.js is
 * not fetched at all until `isAnalyticsConsentGranted()` (utils/consent.ts) is
 * true. No cookieless pings, because there is no tag to send them.
 *
 * WHAT CONSENT GRANTS, SINCE GA4-14. Two independent answers, not one. All four
 * Consent Mode v2 signals are DECLARED on every page load — that is what puts
 * `gcs`/`gcd` on the wire — and which of them are GRANTED follows the visitor's
 * two answers: analytics unlocks `analytics_storage`, advertising unlocks the
 * three ad signals together. See `buildConsentPayload`.
 *
 * Only ANALYTICS boots the tag, and after GA4-14 spoke 3c that is true again
 * for the reason it was true originally: this tag has exactly ONE destination,
 * the GA4 property. The advertising tag layer is utils/adTags.ts, which fires
 * image beacons and runs no vendor script at all — so an advertising grant
 * changes what these hits DECLARE and nothing else about this file.
 *
 * The two factors are independent and both must pass:
 *
 *     may the tag boot  ⇔  !isAnalyticsBlockedPath(path) && isAnalyticsConsentGranted()
 *
 * Independent in the direction that matters: consent CANNOT unlock a blocked
 * route. A visitor who has accepted and then opens /admin still produces zero
 * hits, held by the same `syncGaOptOut` flag PR #699 introduced — which this
 * item does not relax, only tightens (see `syncGaOptOut` below).
 *
 * GA4-03 — THE MODULE ITEM, AND WHAT IT TURNED OUT TO BE.
 *
 * The item was written as "build a central analytics module". That module is
 * this file: GA4-01, GA4-13 and GA4-02 had already built it — one API for every
 * hit, one bootstrap, one route guard, one sanitizer, one page-view sender above
 * both routers. So GA4-03 EXTENDS it rather than replacing it, and adds the four
 * things the audit asked for that were genuinely missing:
 *
 *   1. ENV GATING. The measurement ID comes from `VITE_GA_MEASUREMENT_ID` and is
 *      no longer in the repo. No ID ⇒ every entry point below is a no-op, so a
 *      developer's localhost and every preview deployment measure nothing at all
 *      rather than measuring production. `GA_ENABLED` is the switch.
 *   2. `/unsubscribe` ON THE BLOCKLIST — a long-lived, replayable bearer token in
 *      a query string, with the same Enhanced Measurement exposure GA4-13 found.
 *   3. PATH NORMALIZATION for private and opaque ids, on REPORTED values only.
 *      `normalizeAnalyticsPath` never touches the dedupe key.
 *   4. ROLE-BASED ADMIN EXCLUSION. The route blocklist covers the admin screens;
 *      it cannot cover an administrator working on `/agent*`, `/messages` or
 *      `/agent/edit-listing/:id`, which are measured for everybody else.
 *      `setGaViewerRole` is the third factor on the opt-out flag.
 *
 * And one bug the audit surfaced on the way: the make-good page view owed by a
 * consent grant was re-sent on every subsequent grant, so "Cookie settings" →
 * Accept counted one screen twice. `lastSentPath` is the fix.
 *
 * WHAT GA4-03 IS NOT. It does not migrate the event call sites — that is GA4-04+
 * — and it does not touch the Consent Mode payloads, which are GA4-02's.
 */

import {
  getConsentSnapshot,
  isAdvertisingConsentGranted,
  isAnalyticsConsentGranted,
  onConsentChange,
  type ConsentSnapshot,
  type ConsentStatus,
} from './consent';
// GA4-03 (PR #702 review): the cached-session key, imported rather than
// re-typed. config/constants.ts has no imports of its own, so this cannot drag
// `import.meta.env` into a module the bare-`tsx` suites evaluate directly.
import { STORAGE_KEYS } from '../config/constants';

/**
 * The only query params allowed to reach Google. Everything else — auth tokens,
 * invite codes, internal ids, whatever a future feature puts in the URL — is
 * dropped by construction rather than by blocklist, so a new sensitive param
 * cannot leak by being forgotten here.
 */
export const GA_ALLOWED_QUERY_PARAMS: readonly string[] = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
];

/**
 * Routes that must produce no GA4 traffic at all.
 *
 * A prefix matches the path itself and anything below it, so `/admin` covers
 * `/admin/blog/edit/:id`. Kept in sync with App.tsx: `/auth/callback`
 * (AuthCallback, token_hash in the query), `/reset-password` (ResetPassword,
 * recovery token in the fragment), and the `/admin` route group.
 *
 * `/admin-login` is here as well, and is the one entry the item's path list did
 * not name. It is not a subroute of `/admin` — it is where `/admin` SENDS you:
 * components/ProtectedRoute.tsx:97 bounces any non-admin visitor there, so
 * without this entry loading /admin still produces a page view, just labelled
 * /admin-login. It is an admin screen by any reading, and measuring it carries
 * no value the rest of the item is trying to preserve.
 */
export const GA_BLOCKED_PATH_PREFIXES: readonly string[] = [
  '/auth/callback',
  '/reset-password',
  '/admin',
  '/admin-login',
  // GA4-03. `/unsubscribe?token={uuid}&type=saved_searches` is linked from every
  // saved-search digest email, and pages/Unsubscribe.tsx says out loud what that
  // token is: "No auth: the opaque token in
  // notification_preferences.unsubscribe_token is the credential", and it is NOT
  // rotated on use. So it is a long-lived, replayable bearer token sitting in a
  // query string — the same shape as the `token_hash` leak GA4-13 caught in
  // production, and with the same escape hatch: the sanitizer keeps it out of
  // `page_location`, but gtag.js's own Enhanced Measurement listeners read
  // `document.location.href` verbatim and never consult this module. Not booting
  // the tag there is the only thing that closes it.
  //
  // Unlike the four above, this one is NOT currently a whitelisted SPA route
  // (src/pages/[[catchAll]]/+data.tsx), so a cold load 404s. That is a separate
  // bug, logged separately, and deliberately not fixed here — see
  // ALLOWED_NON_SPA_BLOCKED in pages/__tests__/gaGuard.test.ts.
  '/unsubscribe',
  // THE CASE PAGES (Purchase Room build brief, R22). `/purchases/<case id>` is
  // a buyer's Purchase Room, `/advisor` and `/advisor/cases/<case id>` are the
  // advisor console. The case id in the path names one buyer's purchase,
  // and these screens open signed document links, so the whole of both
  // prefixes is blocked rather than normalised. Normalising is the `/messages`
  // remedy and it governs only the hits the app sends: gtag.js's Enhanced
  // Measurement listeners would still report `document.location.href`
  // verbatim (see GA_PATH_NORMALIZATION_RULES below). Nothing is lost by it:
  // the case funnel is counted from `case_events` in the database, not in GA4.
  // The prefix rule keeps `/purchases-guide` and `/advisory` measured.
  //
  // Like `/unsubscribe`, neither is in SPA_ROUTES, but for a different reason:
  // both are served by their own Vike shells (`src/pages/purchases/@id/`,
  // `src/pages/advisor/`; brief R18.2), not by the catch-all, and SPA_ROUTES
  // stays untouched (R18.3). That is the design rather than a bug, so they are
  // PERMANENT entries in ALLOWED_NON_SPA_BLOCKED in
  // pages/__tests__/gaGuard.test.ts. Being a Vike page changes nothing on the
  // wire: a cold load still renders the root `+Head.tsx`, which asks this list
  // before it emits the tag, and a client-side move in from a measured page
  // raises the opt-out flag in `syncGaOptOut`.
  '/purchases',
  '/advisor',
];

/**
 * GA4-14 spoke 3 — the ONLY surfaces an advertising tag may run on.
 *
 * A WHITELIST, DELIBERATELY, and the opposite construction from the blocklist
 * above. `GA_BLOCKED_PATH_PREFIXES` protects routes that carry a credential:
 * naming them is enough, because measuring everything else is the product's
 * intent. Advertising is the reverse — the promise made in Privacy Policy v3
 * §4.5 is that "advertising tags run only on public marketing pages", so the
 * safe default has to be OFF and a new route has to be added here on purpose to
 * be advertised on. A route added to the app tomorrow is out of the audience by
 * construction rather than by somebody remembering to block it.
 *
 * THE BLOCKLIST STILL WINS. `isAdsEligiblePath` asks
 * `!isAnalyticsBlockedPath(p) && matchesAllowlist(p)`, in that order, so a
 * route that ever lands in both is refused. Nothing overlaps today; the
 * precedence exists so that a future blocklist entry cannot be silently
 * outvoted by an allowlist entry above it.
 *
 * WHAT IS NOT HERE, AND WHY THAT IS THE WHOLE POINT:
 *
 *   - `/agent` and everything under it. The private agent dashboard
 *     (`App.tsx:361-392`) and the PUBLIC agent profile (`/agent/<slug>`,
 *     `src/pages/agent/@slug/`) share one path namespace, and a whitelist that
 *     admitted the profile would be one typo away from admitting the
 *     dashboard. `/agents` — the public directory, `src/pages/agents/` — is a
 *     DIFFERENT path, not a parent of it: under the prefix rule below
 *     `/agents` neither matches nor is matched by `/agent`, which is what lets
 *     the directory in while the namespace stays out. PO-confirmed 2026-08-31.
 *   - `/join/<code>`. `/join` is the evergreen agent landing page and is here;
 *     the code form is a campaign redirect carrying an opaque identifier, so
 *     `/join` is EXACT-MATCH ONLY (see `ADS_EXACT_MATCH_ONLY`).
 *   - `/blog`, `/diaspora`, `/about`, `/faq`, `/contact`, `/careers`,
 *     `/syndication`, `/syndicated/<id>`, `/tools/*`, `/privacy`, `/terms`.
 *     All public, none of them named in the approved policy text. The rule for
 *     this list is that an unsure surface stays out, so they are out.
 *   - Every route behind `ProtectedRoute`/`AgentRoute`/`AdminRoute`, which are
 *     excluded by not being named rather than by a second guard.
 */
export const ADS_ELIGIBLE_PATH_PREFIXES: readonly string[] = [
  // Home — src/pages/index/+Page.tsx. Exact-match only; a prefix match on '/'
  // would admit every route in the app.
  '/',
  // The SEO hubs — src/pages/for-sale/@type[/@city]/+Page.tsx.
  '/for-sale',
  // Property detail — src/pages/property/@slug/+Page.tsx, App.tsx:271.
  '/property',
  // Guides — src/pages/guides[/@slug]/+Page.tsx.
  '/guides',
  // Pricing — src/pages/pricing/+Page.tsx.
  '/pricing',
  // The public agent directory — src/pages/agents/+Page.tsx. NOT /agent.
  '/agents',
  // The evergreen agent landing page — App.tsx:251. Exact-match only.
  '/join',
];

/**
 * The entries above that match a path EXACTLY and never as a prefix.
 *
 * `/` because a prefix match on it is the whole site. `/join` because
 * `/join/<code>` is a campaign identifier, and constraint: no dynamic segment
 * enters an ad audience.
 */
export const ADS_EXACT_MATCH_ONLY: readonly string[] = ['/', '/join'];

/**
 * The GA4 measurement ID.
 *
 * GA4-13 gave the tag a second place to boot from — `src/pages/+Head.tsx` for
 * the server-rendered bootstrap on allowed routes, `ensureGtagBootstrapped()`
 * below for the client-side one — so the ID lives here rather than being
 * written out twice. It is still hardcoded and still unconditional; making it
 * env-driven is GA4-02/GA4-03, not this item.
 *
 * GA4-03 — IT IS NOW ENV-DRIVEN, and the ID is no longer in the repo.
 *
 * WHY. A hardcoded ID is measured from everywhere the code runs: a developer's
 * localhost, every Vercel preview deployment, every CI run that boots the app.
 * All of it landed in the one production property, which is the documented
 * source of the github.com referral traffic in the GA4 audit. There is no
 * separate preview stream — the locked decision is that a build with no ID
 * measures NOTHING, rather than measuring somewhere else.
 *
 * RESOLUTION ORDER. `import.meta.env` first, because that is what a Vite build
 * inlines and what the browser bundle actually has; `process.env` second, for
 * the two contexts where `import.meta.env` does not exist — the bare `tsx` test
 * runner (no Vite transform at all) and the Vike SSR runtime. Both are read
 * defensively: `typeof` guards keep this from throwing in a context that has
 * neither, which matters because this module is imported at the top of
 * `src/pages/+Head.tsx` and evaluating it must never take a page down.
 */
function resolveMeasurementId(): string {
  const fromVite =
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_GA_MEASUREMENT_ID) || '';
  const fromNode =
    (typeof process !== 'undefined' && process.env?.VITE_GA_MEASUREMENT_ID) || '';
  return String(fromVite || fromNode || '').trim();
}

export const GA_MEASUREMENT_ID = resolveMeasurementId();

/**
 * The master switch: is there a usable measurement ID at all?
 *
 * Shape-checked rather than merely non-empty, so a truncated or placeholder
 * value ('', 'TODO', 'G-') cannot half-enable the tag — a malformed ID would
 * otherwise produce a loader request to googletagmanager.com that 404s, which
 * is exactly the "zero hits from preview" guarantee failing quietly.
 *
 * Every entry point in this file is gated on it, so `false` means: no consent
 * snippet, no bootstrap snippet, no loader, no config, no hit, no opt-out flag.
 * The ONE thing it does not gate is the consent banner. Asking permission to
 * measure is a legal surface that stands on its own; whether the tag happens to
 * be wired up in this build is our problem, not the visitor's. See
 * `shouldPrompt` in src/components/ConsentBanner.tsx, which reads the route and
 * the stored status and nothing else.
 */
export const GA_ENABLED = /^G-[A-Z0-9]+$/.test(GA_MEASUREMENT_ID);

/** The gtag.js loader URL, in both bootstraps. */
export const GA_SCRIPT_SRC = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;

/**
 * GA4-14 spoke 3 — the Google Ads conversion/remarketing ID.
 *
 * Env-driven for exactly the reason `GA_MEASUREMENT_ID` is (GA4-03 doctrine): a
 * build with no ID measures NOTHING, and that includes advertising. localhost,
 * every Vercel preview and every CI run therefore fire no ad beacon at all, and
 * that is the designed state rather than a misconfiguration to fix.
 *
 * GA4-14 spoke 3c (D14) — IT IS NO LONGER A `config` TARGET. Nothing in this
 * file configures gtag.js for it, and nothing anywhere loads a second gtag.js
 * for it: utils/adTags.ts derives the numeric half of this ID and puts it in
 * the path of Google's documented remarketing image. The `AW-` shape check
 * below is kept because it is what a malformed value has to fail.
 */
function resolveGoogleAdsId(): string {
  const fromVite =
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_GOOGLE_ADS_ID) || '';
  const fromNode = (typeof process !== 'undefined' && process.env?.VITE_GOOGLE_ADS_ID) || '';
  return String(fromVite || fromNode || '').trim();
}

export const GOOGLE_ADS_ID = resolveGoogleAdsId();

/**
 * Shape-checked, like `GA_ENABLED`, so a placeholder cannot half-enable the
 * tag. `AW-` followed by digits is the only form Google Ads issues.
 */
export const ADS_ENABLED = /^AW-\d+$/.test(GOOGLE_ADS_ID);

/**
 * The config both bootstraps run.
 *
 * `send_page_view: false` is GA4-01's: it retires gtag's automatic hit
 * (including the history-change one) so the guarded, sanitized page view below
 * is the only one the app sends.
 */
export const GA_CONFIG_PARAMS: Readonly<Record<string, boolean>> = { send_page_view: false };

/**
 * GA4's own runtime opt-out flag for this measurement ID.
 *
 * GA4-13 (review follow-up): not booting the tag on a blocked route only covers
 * a LANDING on one. Once the tag has booted on an allowed page, its Enhanced
 * Measurement listeners are attached to the document and survive every
 * client-side navigation after that — and `/admin` is reachable by React Router
 * link from components/Navbar.tsx:148, pages/Profile.tsx:137 and
 * components/BottomNav.tsx:285. A scroll or click there would still have sent
 * `dl = …/admin`, because nothing re-renders `+Head` on that path and the
 * listeners are not ours to remove.
 *
 * `window['ga-disable-<ID>']` is the mechanism gtag.js itself provides for this:
 * it is read on the send path, so it suppresses the script's own hits as well as
 * the app's, and Google's documented opt-out button toggles it at runtime long
 * after the tag has loaded. `syncGaOptOut()` below keeps it equal to
 * `isAnalyticsBlockedPath(current path)` — set, not latched, so leaving a
 * blocked route restores measurement.
 */
export const GA_DISABLE_FLAG = `ga-disable-${GA_MEASUREMENT_ID}`;

/**
 * The inline bootstrap, as the source `src/pages/+Head.tsx` injects verbatim.
 *
 * It is generated rather than typed out in the head so that the two bootstraps
 * cannot drift: the same ID and the same config params reach the server-rendered
 * one and `ensureGtagBootstrapped()` alike. pages/__tests__/gaGuard.test.ts pins
 * that they agree.
 */
export function gaBootstrapSnippet(): string {
  // GA4-03: no ID, no snippet. Returning '' rather than a snippet built from an
  // empty ID is what stops `+Head.tsx` emitting `gtag('config', '', …)`.
  if (!GA_ENABLED) return '';

  return [
    'window.dataLayer = window.dataLayer || [];',
    'function gtag(){dataLayer.push(arguments);}',
    "gtag('js', new Date());",
    `gtag('config', '${GA_MEASUREMENT_ID}', ${JSON.stringify(GA_CONFIG_PARAMS)});`,
  ].join('\n');
}

/**
 * The four Consent Mode v2 signals, in the order they are written to the page.
 *
 * All four, not just `analytics_storage`. GA4 reports a consent-signal warning
 * on a property whose hits carry no `gcs`/`gcd`, and the ad signals are part of
 * what those parameters encode — a tag that declares only the analytics one is
 * still an incompletely-signalled tag.
 *
 * GA4-14 — DECLARED IS NOT GRANTED, and the four now disagree. GA4-02's note
 * here said "there are no ad tags on this site, so the four never disagree:
 * they are denied together and granted together." That premise died with the
 * two-category banner (recon R11). All four are still DECLARED on every page
 * load, which is what earns the `gcs`/`gcd` parameters; which of them are
 * GRANTED is now a function of two independent answers — see
 * `buildConsentPayload`.
 */
export const GA_CONSENT_SIGNALS = [
  'ad_storage',
  'ad_user_data',
  'ad_personalization',
  'analytics_storage',
] as const;

/**
 * The three the ADVERTISING category speaks for, as one unit.
 *
 * Google splits ad consent three ways — storage, sending user data, and
 * personalisation — but the banner asks one question about advertising, so the
 * three move together and are never granted piecemeal. Splitting them later
 * would mean a third toggle and a third disclosure, not a code change here.
 */
export const GA_AD_CONSENT_SIGNALS = ['ad_storage', 'ad_user_data', 'ad_personalization'] as const;

function consentPayload<T extends 'granted' | 'denied'>(value: T): Record<string, T> {
  return Object.fromEntries(GA_CONSENT_SIGNALS.map((signal) => [signal, value])) as Record<string, T>;
}

/** What `gtag('consent', 'default', …)` always says, on every page load. */
export const GA_CONSENT_DENIED: Readonly<Record<string, 'denied'>> = consentPayload('denied');

/**
 * What `gtag('consent', 'update', …)` says, derived from the two answers the
 * visitor actually gave.
 *
 * GA4-14 replaced the `GA_CONSENT_ACCEPTED` constant with this function. A
 * constant could only describe one shape of consent, and there are now four:
 * the banner asks about analytics and advertising independently, so "accepted"
 * is no longer a single state to name.
 *
 *   analytics   ⇒ analytics_storage
 *   advertising ⇒ ad_storage + ad_user_data + ad_personalization
 *
 * DERIVED FROM THE DENIED BASELINE rather than written out, exactly as the
 * constant was, so a fifth signal added to `GA_CONSENT_SIGNALS` arrives denied
 * instead of arriving granted by being forgotten. Only 'granted' grants:
 * 'unset' and 'denied' both leave the signal at its denied default, because the
 * absence of an answer is not permission.
 *
 * WHAT THIS DOES AND DOES NOT DO. It declares consent state to gtag.js, and
 * that is all it does. It does NOT load an advertising tag: there is no
 * advertising destination on this tag and no vendor script anywhere on the site
 * (GA4-14 spoke 3c). An advertising grant therefore changes the `gcs` parameter
 * on GA4 hits here, and — in the other module entirely — whether
 * utils/adTags.ts's image beacons are allowed to fire.
 */
export function buildConsentPayload(
  analytics: ConsentStatus,
  advertising: ConsentStatus,
): Readonly<Record<string, 'granted' | 'denied'>> {
  const payload: Record<string, 'granted' | 'denied'> = { ...GA_CONSENT_DENIED };

  if (analytics === 'granted') payload.analytics_storage = 'granted';
  if (advertising === 'granted') {
    for (const signal of GA_AD_CONSENT_SIGNALS) payload[signal] = 'granted';
  }

  return payload;
}

/**
 * The payload for what the visitor has agreed to RIGHT NOW.
 *
 * Reads storage rather than taking the snapshot it was handed, because the two
 * callers reach here by different routes — one from a consent notification that
 * carries a snapshot, one from a cold bootstrap that has none — and a tag that
 * declares a state the record does not hold is worse than one extra read. It is
 * one `localStorage` read and one `JSON.parse`, on a path that runs at most
 * once per consent change.
 */
function currentConsentPayload(): Readonly<Record<string, 'granted' | 'denied'>> {
  const snapshot = getConsentSnapshot();
  return buildConsentPayload(snapshot.analytics, snapshot.advertising);
}

/**
 * The consent half of the bootstrap, which must run BEFORE `config`.
 *
 * Order is the whole point. `gtag('consent', 'default', …)` has to be on the
 * dataLayer before the first `config` so that gtag.js reads it while deciding
 * what the very first hit may carry; a default that arrives afterwards is a
 * default for the next hit, and the first one goes out unsignalled. That is
 * what leaves the GA4 property warning about missing consent signals even
 * though the code "has consent mode in it".
 *
 * It carries its own copy of the dataLayer/gtag shim because it is emitted as
 * its own `<script>`, ahead of the loader and ahead of `gaBootstrapSnippet()` —
 * which is exactly the shape Google's own documentation uses, and which keeps
 * `gaBootstrapSnippet()` byte-for-byte what GA4-13 pinned it to. Both halves
 * are classic scripts sharing one global scope, so re-running two idempotent
 * lines costs nothing and lets either half stand alone.
 */
export function gaConsentSnippet(analytics: ConsentStatus, advertising: ConsentStatus): string {
  // GA4-03: same rule as the config snippet. Consent Mode commands describe a
  // tag; with no tag to describe they are noise on the dataLayer.
  if (!GA_ENABLED) return '';

  const lines = [
    'window.dataLayer = window.dataLayer || [];',
    'function gtag(){dataLayer.push(arguments);}',
    `gtag('consent', 'default', ${JSON.stringify(GA_CONSENT_DENIED)});`,
  ];

  // Reflects the STORED state, so the page that boots the tag also says out
  // loud why it was allowed to — and exactly what it was allowed to do.
  //
  // BOTH answers must be known, not just one. An update naming only the
  // category that happens to be decided would declare the other one denied on
  // the visitor's behalf, and 'unset' is not a decision — it is the state in
  // which the banner is still on screen. So a half-answered visitor gets the
  // default and nothing else, and the update arrives from `applyConsentChange`
  // the moment they finish answering.
  //
  // GA4-14 note: this branch does not run on the server and cannot. Consent is
  // localStorage-only by locked decision (no consent cookie — see the dead
  // branch in src/pages/+Head.tsx), so a server render sees both categories
  // 'unset' every time. That is exactly why the snippet is still safe to emit
  // from the head: server-side it can only ever say "denied".
  if (analytics !== 'unset' && advertising !== 'unset') {
    lines.push(
      `gtag('consent', 'update', ${JSON.stringify(buildConsentPayload(analytics, advertising))});`,
    );
  }
  return lines.join('\n');
}

/**
 * Base for `new URL()` so a relative input still parses. Both call sites pass an
 * absolute URL (`window.location.href`, `document.referrer`), so this origin is
 * not emitted in practice; it is the site's canonical www origin rather than the
 * apex so that it stays correct if it ever is — see pages/__tests__/
 * seoCanonicalOrigin.test.ts, which forbids the apex form repo-wide.
 */
const URL_PARSE_BASE = 'https://www.afriqahome.com';

/**
 * The URL this document was opened with, captured when the module is first
 * evaluated — i.e. while the bundle loads, before React hydrates.
 *
 * It is not the same as reading `window.location` from the effect that starts
 * tracking. Vike's client router rewrites the address bar to the route's plain
 * path on hydration (a `/?utm_source=x` landing becomes `/`), and Home.tsx
 * writes filter state over it as well. Both race the first page view, and the
 * loser is campaign attribution: GA4 would be told the visit landed on `/`.
 * Reading the URL this early wins that race by construction.
 */
const INITIAL_URL = typeof window === 'undefined' ? '' : window.location.href;
const INITIAL_REFERRER = typeof document === 'undefined' ? '' : document.referrer;

/**
 * True when `pathname` is a route no GA4 hit may be sent from.
 *
 * Takes the path as an argument rather than reading `window` so it is safe to
 * call during SSR (Vike renders `+Head`/`+Layout` on the server) and testable.
 */
export function isAnalyticsBlockedPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;

  // Normalise a trailing slash so `/admin/` matches `/admin`.
  let path = pathname.toLowerCase();
  if (path.length > 1) path = path.replace(/\/+$/, '') || '/';

  return GA_BLOCKED_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

/**
 * Normalise a path the one way this module normalises paths.
 *
 * Extracted from `isAnalyticsBlockedPath` so the allowlist below cannot drift
 * from the blocklist on case or on a trailing slash — a route that reads as
 * `/admin` to one predicate and `/Admin/` to the other is exactly how a
 * precedence rule stops meaning anything.
 */
function normalisedPathFor(pathname: string): string {
  let path = pathname.toLowerCase();
  if (path.length > 1) path = path.replace(/\/+$/, '') || '/';
  return path;
}

/**
 * GA4-14 spoke 3 — may an advertising tag run on this route?
 *
 * Same input and output typing as `isAnalyticsBlockedPath`, and the same
 * normalisation, so the two can be read side by side.
 *
 * THE BLOCKLIST WINS, and it is asked first. Eligibility is
 * `!isAnalyticsBlockedPath(p) && matchesAllowlist(p)`: a route in both lists is
 * refused, always, whatever order the entries happen to be in.
 *
 * Prefix semantics are the blocklist's — `path === prefix` or
 * `path.startsWith(prefix + '/')` — except for the entries in
 * `ADS_EXACT_MATCH_ONLY`, which never match as prefixes. That exception is what
 * separates `/join` from `/join/<code>` and stops `/` from meaning everything.
 *
 * Takes the path as an argument rather than reading `window`, like every other
 * predicate here, so it is safe during SSR and testable without a DOM.
 */
export function isAdsEligiblePath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;

  const path = normalisedPathFor(pathname);

  // Asked first and separately, so this precedence is visible rather than
  // implied by the contents of the two lists.
  if (isAnalyticsBlockedPath(path)) return false;

  return ADS_ELIGIBLE_PATH_PREFIXES.some((prefix) => {
    if (ADS_EXACT_MATCH_ONLY.includes(prefix)) return path === prefix;
    return path === prefix || path.startsWith(`${prefix}/`);
  });
}

/**
 * Reduce a URL to what GA4 is allowed to see: origin + path, the allowlisted
 * campaign params, and no fragment.
 *
 * Returns '' for anything unparseable (or an empty input, e.g. a missing
 * `document.referrer`) so callers can simply omit the parameter.
 */
export function sanitizeAnalyticsUrl(rawUrl: string | null | undefined): string {
  if (!rawUrl) return '';

  let url: URL;
  try {
    url = new URL(rawUrl, URL_PARSE_BASE);
  } catch {
    return '';
  }

  const kept = new URLSearchParams();
  for (const key of GA_ALLOWED_QUERY_PARAMS) {
    for (const value of url.searchParams.getAll(key)) kept.append(key, value);
  }

  const query = kept.toString();
  url.search = query ? `?${query}` : '';
  // Always dropped: /reset-password receives the Supabase recovery token here.
  url.hash = '';

  return url.toString();
}

/**
 * GA4-03 — the dynamic-segment rules, longest prefix first.
 *
 * ONLY private or opaque identifiers. `/messages/<uuid>` names one buyer↔agent
 * conversation, `/agent/edit-listing/<uuid>` and `/syndicated/<uuid>` name a
 * row, `/join/<code>` names a campaign code. Reported raw, each one is both a
 * high-cardinality page path (GA4 reports become a list of one-hit URLs) and,
 * for the first three, a way to identify what a specific visitor was looking at.
 *
 * Public slug pages are deliberately NOT here — `/property/:slug`,
 * `/agent/:slug`, `/blog/:slug`, `/guides/:slug`, `/careers/:slug`,
 * `/for-sale/:type[/:city]`. Those slugs ARE the content report: collapsing them
 * would throw away the only thing GA4 is being asked to measure on this site.
 *
 * Ordered longest-prefix-first so `/agent/edit-listing/x` can never be read as a
 * one-segment `/agent` rule if one is ever added above it.
 *
 * WHAT THIS DOES NOT COVER, STATED PLAINLY. Normalization governs the hits the
 * APP sends. It cannot govern the ones gtag.js sends by itself: once
 * `gtag('config', …)` has run, its Enhanced Measurement listeners fire with
 * `dl = document.location.href` verbatim, so a scroll on
 * /messages/<uuid> still hands Google the raw id — the same escape hatch GA4-13
 * documented, and the reason `/unsubscribe` had to be BLOCKED rather than
 * merely sanitized.
 *
 * These four routes are deliberately not blocked: they are real screens whose
 * traffic the product wants counted, and blocking is the remedy for a
 * CREDENTIAL in the URL, not for an opaque id. Closing this residue as well
 * would mean either adding them to the blocklist (and measuring nothing there)
 * or turning Enhanced Measurement off at the stream level in the GA4 console —
 * both product decisions, neither of them GA4-03's to make. Logged as
 * follow-up rather than silently assumed away.
 *
 * CASE-PAGE REFERRER (Purchase Room build brief, R22). The case pages are
 * blocked (see GA_BLOCKED_PATH_PREFIXES) and still need rules, because a
 * blocked page is never `page_location` but is `page_referrer` on the next
 * page. The tracker reports the previous on-screen URL there, blocked routes
 * included, and the sanitizer drops only the query and the fragment, so
 * without these the case id in the path reached GA4 one screen later. Found
 * while blocking them (PR #748).
 */
const GA_PATH_NORMALIZATION_RULES: readonly { prefix: readonly string[]; placeholder: string }[] = [
  { prefix: ['agent', 'edit-listing'], placeholder: ':id' },
  { prefix: ['advisor', 'cases'], placeholder: ':caseId' },
  { prefix: ['messages'], placeholder: ':conversationId' },
  { prefix: ['syndicated'], placeholder: ':id' },
  { prefix: ['join'], placeholder: ':code' },
  { prefix: ['purchases'], placeholder: ':caseId' },
];

/**
 * CASE-PAGE REFERRER — the prefixes under which a path no rule describes is
 * reported as the bare prefix, never as itself.
 *
 * Every other prefix keeps the GA4-03 behaviour of leaving an unknown shape
 * raw. Here the default is the other way round: every screen under these two
 * belongs to one buyer's purchase, so a shape added later (`/purchases/<id>/
 * documents`, `/advisor/cases/<id>/steps`) reports as `/purchases` or
 * `/advisor` until somebody writes a rule for it. Each entry is also in
 * GA_BLOCKED_PATH_PREFIXES, pinned in pages/__tests__/gaGuard.test.ts.
 */
export const GA_FAIL_CLOSED_PATH_PREFIXES: readonly string[] = ['/purchases', '/advisor'];

/**
 * Collapse a dynamic segment to its parameter name, for REPORTING ONLY.
 *
 * NEVER used as a dedupe key. `lastTrackedPath` stays keyed on the raw path, so
 * `/messages/a` → `/messages/b` is still two navigations and still two page
 * views; it is only what GA4 is TOLD that collapses. Normalizing the key would
 * silently merge those two into one and lose the second hit.
 *
 * Matching is exact on the prefix segments and requires EXACTLY ONE trailing
 * segment. `/messages` on its own is left alone — it is a real screen with its
 * own report line — and so is anything deeper, which no route in the app
 * produces today and which would be a new shape rather than an id. The
 * exception is GA_FAIL_CLOSED_PATH_PREFIXES (case-page referrer): there, a
 * path no rule matches reports as the bare prefix.
 *
 * Prefix comparison is case-insensitive (`isAnalyticsBlockedPath` normalises
 * case too, and a route reached through a differently-cased link is the same
 * route); the returned form is the canonical lowercase one, so two spellings of
 * the same private path cannot become two report lines.
 */
/**
 * Is this origin ours?
 *
 * The live origin in a browser, so a preview deployment and production both
 * answer for themselves; the canonical origin otherwise, which is what SSR and
 * the test runner have. Never a substring match — `afriqahome.com.evil.test`
 * must not read as ours.
 */
function isOwnOrigin(origin: string): boolean {
  if (typeof window !== 'undefined' && window.location?.origin) {
    if (origin === window.location.origin) return true;
  }
  return origin === new URL(URL_PARSE_BASE).origin;
}

export function normalizeAnalyticsPath(pathname: string | null | undefined): string {
  if (!pathname) return '';

  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return pathname;

  for (const rule of GA_PATH_NORMALIZATION_RULES) {
    // Exactly one trailing segment beyond the prefix — no more, no less.
    if (segments.length !== rule.prefix.length + 1) continue;
    const matches = rule.prefix.every(
      (segment, i) => segments[i].toLowerCase() === segment,
    );
    if (matches) return `/${rule.prefix.join('/')}/${rule.placeholder}`;
  }

  // Case-page referrer: fail closed. Lowercased like the rules above, so
  // `/Advisor/x` cannot become a second report line or keep its raw tail.
  const head = `/${segments[0].toLowerCase()}`;
  if (GA_FAIL_CLOSED_PATH_PREFIXES.includes(head)) return head;

  return pathname;
}

/**
 * GA4-03 — the one URL every reported value is built from: sanitized, then
 * normalized.
 *
 * The two steps are separate functions because they answer different questions —
 * the sanitizer decides what a URL may CARRY (allowlisted campaign params, no
 * fragment), the normalizer decides what a path may SAY — but nothing should
 * ever apply one without the other, or a page view and an event fired on the
 * same screen would disagree about where they happened.
 *
 * Returns '' for anything the sanitizer rejects, so callers can drop the hit or
 * omit the parameter exactly as they did before.
 */
export function reportableUrl(rawUrl: string | null | undefined): string {
  const sanitized = sanitizeAnalyticsUrl(rawUrl);
  if (!sanitized) return '';

  try {
    const url = new URL(sanitized);
    // SAME-ORIGIN ONLY. `page_referrer` is an external URL on the first hit of
    // every visit (`document.referrer`), and these rules describe THIS app's
    // routes. Applied blindly they would rewrite a third-party referrer that
    // happens to share a path shape — https://example.com/join/abc becoming
    // https://example.com/join/:code — into a URL that never existed, which is
    // a fabricated referrer rather than a redacted one.
    if (isOwnOrigin(url.origin)) url.pathname = normalizeAnalyticsPath(url.pathname);
    return url.toString();
  } catch {
    return '';
  }
}

/**
 * GA4-14 spoke 3 (D9-A) — is this URL one an ad vendor may be handed AT ALL?
 *
 * True iff the sanitizer would not change it: no query params beyond
 * `GA_ALLOWED_QUERY_PARAMS` (the five `utm_*` plus `gclid`) and no fragment.
 *
 * SKIP, DO NOT STRIP. This is the one place where the ad tags are deliberately
 * stricter than GA4, and it OUTLIVED the reason it was written, which is worth
 * saying out loud rather than letting the rule look arbitrary.
 *
 * The original reason was that a vendor script reads `document.location.href`
 * verbatim for its own pings, with neither sanitizer nor normalizer in that
 * path — so cleaning the URL we passed in bought nothing. GA4-14 spoke 3c
 * removed both vendor scripts, and the image beacons utils/adTags.ts fires now
 * carry only the URL this module sanitized. That argument is therefore gone.
 *
 * The rule stays, and is now the SUBSTANTIVE one it always also was: a visitor
 * on `/?q=westlands` is on a search-results screen, and putting that screen in
 * a remarketing audience under the address `/` would advertise on the search
 * while telling the vendor it was the home page. A URL the sanitizer would have
 * had to clean is a screen this page view must not happen on at all — for BOTH
 * vendors, together — rather than one whose address gets tidied.
 *
 * Compared against the sanitizer's own output rather than re-deriving the rule,
 * so the allowlist cannot drift between the two.
 */
export function urlIsAdSafe(rawUrl: string | null | undefined): boolean {
  if (!rawUrl) return false;

  const sanitized = sanitizeAnalyticsUrl(rawUrl);
  if (!sanitized) return false;

  // The sanitizer always drops the fragment, so comparing raw against sanitized
  // would report every `#anchor` URL as unsafe on the strength of the fragment
  // alone. Strip it here too and let the QUERY be what the comparison is about;
  // a fragment is still refused, one line below, because a fragment is where
  // /reset-password's recovery token lives.
  let url: URL;
  try {
    url = new URL(rawUrl, URL_PARSE_BASE);
  } catch {
    return false;
  }
  if (url.hash) return false;

  return sanitized === url.toString();
}

/**
 * GA4-14 spoke 3 — is this document running inside the native shell?
 *
 * A RUNTIME check on `window.Capacitor`, deliberately, and not
 * `src/lib/native.ts`'s `isNative()`. Two reasons, and the second is the one
 * that matters:
 *
 *   1. `isNative()` reads `import.meta.env.VITE_BUILD_TARGET` — a fact about
 *      the BUNDLE. Importing it here would also drag `@capacitor/core`,
 *      `@capacitor/app`, `@capacitor/status-bar` and the push stack into a
 *      module the bare-`tsx` suites evaluate directly.
 *   2. `capacitor.config.ts` lists `www.afriqahome.com` in `allowNavigation`,
 *      so the app's own webview can end up running the WEB bundle — where
 *      `isNative()` is correctly false and this check is correctly true. That
 *      is the case a build-target flag cannot see, and it is the case where an
 *      ad tag inside the app would actually happen.
 *
 * Policy §4.5/§11 promise no advertising SDK in the apps. Today that is true by
 * accident of the entry point (`index.native.html` → `App.tsx` mounts neither
 * `+Head.tsx` nor `+Layout.tsx`, so nothing here is ever started). This makes it
 * true by code as well.
 */
export function isNativePlatform(): boolean {
  if (typeof window === 'undefined') return false;
  const capacitor = (window as unknown as Record<string, unknown>).Capacitor as
    | { isNativePlatform?: () => boolean }
    | undefined;
  try {
    return capacitor?.isNativePlatform?.() === true;
  } catch {
    // A shim that throws is not evidence of a browser; refuse rather than
    // advertise into an unknown runtime.
    return true;
  }
}

/**
 * GA4-14 spoke 3 — the single gate BOTH ad vendors pass through.
 *
 * Deliberately independent of GA4: no `GA_ENABLED`, no `window.gtag`, no
 * `isAnalyticsConsentGranted`. A visitor who grants advertising and refuses
 * analytics is advertised to and not measured, which is precisely the split the
 * two-category banner exists to express.
 *
 * The five factors, each of which alone is enough to refuse:
 *
 *   1. `isAdvertisingConsentGranted()` — read from storage on EVERY call, never
 *      cached, so a withdrawal in another tab counts here immediately and the
 *      GPC coercion in utils/consent.ts (D2) is honoured on every read.
 *   2. `isAdsEligiblePath` — the allowlist, blocklist-first.
 *   3. `isGaViewerExcluded()` — staff and admins never enter an audience, for
 *      the same reason they are never measured.
 *   4. `isNativePlatform()` — the apps carry no ad tag, by code.
 *   5. `urlIsAdSafe` — this exact URL, fragment and query included.
 *
 * Callers must ask this immediately before acting, not once at startup.
 */
export function canFireAdTags(): boolean {
  if (typeof window === 'undefined') return false;
  if (isNativePlatform()) return false;
  if (isGaViewerExcluded()) return false;
  if (!isAdvertisingConsentGranted()) return false;
  if (!isAdsEligiblePath(window.location.pathname)) return false;
  return urlIsAdSafe(window.location.href);
}

/**
 * GA4-14 spoke 3b (D12) — THE WHOLE CONJUNCTION, both URLs, one predicate.
 *
 * WHY TWO URLS. The tag layer judges a page view on the URL the document was
 * OPENED with, because the app rewrites the address bar under it: Vike strips
 * the query on hydration, so a landing on `/?q=nairobi+2br` becomes `/`, and
 * pages/Home.tsx writes the filter state into `/` on every filter change. Judge
 * on the live URL alone and the search-results screen is advertised on as if it
 * were the bare home page — the disqualifying part having been removed by the
 * app itself. Judge on the OPENED url alone and the reverse happens, which is
 * what P4 caught in production: the document opened at `/` (eligible), the app
 * performed a `<Navigate replace>` to `/messages` (not eligible), and the ad
 * page view decided for `/` was still in flight.
 *
 * So BOTH must pass, and a mismatch means SKIP:
 *
 *   a) `isAdsEligiblePath(opened path)`      d) `urlIsAdSafe(live href)`
 *   b) `isAdsEligiblePath(live pathname)`    e) blocklist precedence on both
 *   c) `urlIsAdSafe(opened url)`             f) consent, GPC, staff, native
 *
 * (b), (d) and (f) are `canFireAdTags()`. (e) is free: `isAdsEligiblePath` asks
 * the blocklist first, and it is asked here for the opened path as well as by
 * `canFireAdTags` for the live one.
 *
 * CALL IT IMMEDIATELY BEFORE THE VENDOR REQUEST. Spoke 3b had to say "after
 * any lazily-loaded vendor script has arrived", because the decision and the
 * send were separated by a network round trip and the vendor read
 * `document.location.href` for itself at the far end of it. GA4-14 spoke 3c
 * removed that gap entirely: `sendAdPageViews` in utils/adTags.ts evaluates
 * this predicate, builds the beacon URL from the answer and issues the request
 * in one synchronous call stack, so "immediately before" is now literal. Every
 * input is read fresh on every call — consent from storage, the path and href
 * from `window.location` — and nothing here is cached between calls.
 */
export function adPageViewAllowed(openedUrl: string | null | undefined): boolean {
  if (typeof window === 'undefined') return false;
  if (!openedUrl) return false;
  // (a) and (e), on the URL the view is being judged for.
  if (!isAdsEligiblePath(pathOf(openedUrl))) return false;
  // (c) — the query and fragment rule, on that same URL.
  if (!urlIsAdSafe(openedUrl)) return false;
  // (b), (d), (e) and (f) — the live URL and the five-factor gate.
  return canFireAdTags();
}

/**
 * GA4-13 — boot the tag if it is not booted, and report whether it is now.
 *
 * WHY THIS IS NEEDED AT ALL. `src/pages/+Head.tsx` skips the bootstrap when the
 * SSR-requested route is blocked, which is the only thing that stops gtag.js
 * attaching its Enhanced Measurement listeners there. But every exit from a
 * blocked route in this app is a CLIENT-SIDE navigation, so skipping it
 * server-side alone would leave the tag absent for the rest of the visit:
 *
 *   - `/auth/callback` renders inside App.tsx's React Router (App.tsx:268) via
 *     the `[[catchAll]]` SPA page, and every exit is `navigate(...)` —
 *     `navigate('/')` on email verification (pages/AuthCallback.tsx:552),
 *     `navigate('/onboarding')` / `navigate('/agent')` on the OAuth paths
 *     (:507, :509), `navigate('/auth')` behind the "Try Again" button on the
 *     failure screen (:819).
 *   - `/reset-password` exits the same way (pages/ResetPassword.tsx:77, :125).
 *   - `/admin` bounces to `/admin-login` and `/auth` through React Router's
 *     `<Navigate>` (components/ProtectedRoute.tsx:64, :97), and `/admin-login`
 *     leaves by `navigate('/admin')` (pages/AdminLogin.tsx:27, :57).
 *
 * None of those re-run the server render. Vike's own client router would not
 * help either: `+Head` is rendered by `renderToStaticMarkup` during
 * `onRenderHtml` only — `onRenderClient` touches nothing but `title` and `lang`
 * (vike-react 0.6.18, dist/integration/onRenderClient.js) — so a Vike
 * client-side navigation off a blocked route does not re-inject the head
 * either. Hence this.
 *
 * WHY IT IS SAFE. It refuses while the browser is standing on a blocked route,
 * reading the live `window.location` rather than trusting its caller. That is
 * the invariant the whole item rests on: the loader must never be requested
 * from a blocked route, because everything after `gtag('config', ...)` is out of
 * this module's hands.
 *
 * IDEMPOTENCY. The inline part of the bootstrap defines `window.gtag`
 * synchronously — it does not wait for the loader — so `window.gtag` being a
 * function is an exact record of "already booted", whether by the head or by an
 * earlier call here. Nothing else in the app assigns it. An ad blocker that
 * drops googletagmanager.com does not clear it either: the inline snippet still
 * ran, so a blocked loader cannot trick this into injecting a second one.
 */
/**
 * Has the GA4 property had `gtag('config', …)` run on THIS document.
 *
 * `typeof window.gtag === 'function'` answers "is the script booted"; it cannot
 * answer "is the GA4 destination configured". Those were the same question
 * before spoke 3 and are the same question again after spoke 3c — this tag has
 * exactly one destination — but they were NOT the same question in between, and
 * the flag stays because the reason it was added is the reason it is cheap: a
 * `config` that runs twice is the shape GA4-03 fixed, and one that never runs
 * leaves the property unconfigured on a document that thinks it is booted.
 *
 * Module-level rather than per-call for the same reason `lastTrackedPath` is:
 * it describes the document, and it has to outlive a StrictMode remount.
 */
let analyticsDestinationConfigured = false;

/**
 * CASE-PAGE REFERRER — the referrer for every hit on this document that does
 * not name its own, spread into each `config` this module addresses to the
 * property.
 *
 * WHY. gtag.js fills `dr` from `document.referrer` when nothing sets
 * `page_referrer`, and that is the RAW referrer: after a full page load from a
 * room it is `…/purchases/<case id>?tab=documents`. Our page view sends a
 * reportable referrer of its own, but gtag.js's Enhanced Measurement hits
 * (scroll, outbound click, form start) send nothing, so they fell back to the
 * raw one. A `config`-level value is merged into every later hit for this
 * destination, theirs included. It is `reportableUrl`, so it is the same value
 * the landing page view reports.
 *
 * CONFIG, NOT `set`. D10 forbids the global form (pinned in
 * pages/__tests__/gaGuard.test.ts). It goes on EVERY GA4 `config`, not only the
 * first, so the value does not depend on how gtag.js merges a repeat config.
 *
 * Nothing when the document has no referrer: there is nothing to fall back to.
 */
function documentReferrerConfig(): { page_referrer?: string } {
  const pageReferrer = reportableUrl(INITIAL_REFERRER);
  return pageReferrer ? { page_referrer: pageReferrer } : {};
}

function ensureGtagBootstrapped(): boolean {
  // GA4-03: before every other gate, including the environment checks. No
  // measurement ID means there is nothing to boot and nothing to request — this
  // is the line that makes localhost and preview deployments silent.
  if (!GA_ENABLED) return false;
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  if (typeof window.gtag === 'function') return true;
  // GA4-03: staff. BELOW the already-booted check on purpose — this function's
  // answer is "is the tag booted", and for a document where it already is, the
  // honest answer stays `true` so `applyConsentChange` can keep the tag's
  // declared consent state accurate. What is gated is a FRESH boot: the loader
  // request alone hands Google the staff browser's IP and cookies and attaches
  // gtag.js's Enhanced Measurement listeners. The opt-out flag would swallow
  // the hits, but the argument this file makes for `/unsubscribe` applies
  // unchanged — the only thing that reliably stops a tag measuring is the tag
  // never existing. Nothing is sent for an excluded viewer either way:
  // `sendGaPageView` and `canSendHit` both refuse before reaching a `gtag()`.
  if (isGaViewerExcluded()) return false;
  if (isAnalyticsBlockedPath(window.location.pathname)) return false;
  // GA4-02: the second factor, and the one that makes this whole function a
  // no-op for a visitor who has not answered the banner or has declined. It is
  // read from storage on every call rather than captured once, so a decision
  // made in another tab counts here immediately.
  //
  // GA4-14 — ANALYTICS ONLY, deliberately, and spoke 3c made it absolute.
  // Advertising consent must not boot gtag.js: the loader request alone hands
  // Google the visitor's IP and cookies and attaches gtag.js's Enhanced
  // Measurement listeners for a purpose the visitor declined to be measured
  // for. Spoke 3 weakened this by adding a second bootstrap that loaded the
  // loader for an ad grant; spoke 3c deleted it, and the advertising layer now
  // needs no script at all. A visitor who grants advertising and refuses
  // analytics gets NO gtag.js on the document — asserted at the wire in
  // pages/__tests__/consentModeNetwork.test.ts (B2).
  if (!isAnalyticsConsentGranted()) return false;

  // Correct from the tag's first instant, not from the first navigation after it.
  syncGaOptOut(window.location.pathname);

  const dataLayer = (window.dataLayer = window.dataLayer || []);

  // A `function`, not an arrow: gtag.js reads the pushed `arguments` object, and
  // this is the same shim the head's inline snippet defines.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params -- gtag.js reads `arguments`
    dataLayer.push(arguments);
  } as unknown as typeof window.gtag;

  // GA4-02 — CONSENT BEFORE CONFIG, in this order, always. The default has to
  // be on the dataLayer before gtag.js sees its first `config`, or the first
  // hit goes out with no `gcs`/`gcd` and the GA4 property keeps warning that
  // its traffic carries no consent signals.
  //
  // GA4-14: the update carries BOTH answers. Reaching this line means analytics
  // was granted — that is the gate above — but advertising is whatever the
  // visitor actually said, so an analytics-only grant declares the three ad
  // signals denied on the wire, and an accept-all grant declares them granted.
  window.gtag('consent', 'default', { ...GA_CONSENT_DENIED });
  window.gtag('consent', 'update', { ...currentConsentPayload() });

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, { ...GA_CONFIG_PARAMS, ...documentReferrerConfig() });
  analyticsDestinationConfigured = true;

  const loader = document.createElement('script');
  loader.async = true;
  loader.src = GA_SCRIPT_SRC;
  document.head.appendChild(loader);

  return true;
}

/**
 * GA4-03 — who is looking, as far as measurement is concerned.
 *
 * Deliberately the same five values as `hooks/useViewerRole.ts` exports, so the
 * two can be passed to each other without a mapping table. `'unknown'` is the
 * honest default: this module is evaluated long before any session has been
 * resolved, and "we have not been told yet" is not the same claim as
 * "signed out".
 */
export type GaViewerRole = 'unknown' | 'signed-out' | 'buyer' | 'agent' | 'admin';

const GA_EXCLUDED_VIEWER_ROLES: readonly GaViewerRole[] = ['admin'];

let gaViewerRole: GaViewerRole = 'unknown';

/**
 * GA4-03 — the role-based half of admin exclusion.
 *
 * WHY THE ROUTE BLOCKLIST WAS NOT ENOUGH. `GA_BLOCKED_PATH_PREFIXES` covers the
 * admin SCREENS, and that was the whole of GA4-01's claim. It says nothing about
 * an administrator doing administrative work on a route that is measured for
 * everybody else, and this app has three of those: an admin may edit ANY agent's
 * listing at `/agent/edit-listing/:id` (pages/AgentEditListing.tsx:307), reach
 * the agent dashboard at `/agent*` (AgentDashboard.tsx:357,
 * AgentEditListing.tsx:210), and is treated as an agent throughout `/messages`
 * (pages/Messages.tsx:61). Staff traffic on those routes is indistinguishable
 * from a customer's in every report that matters.
 *
 * A SET, NOT A LATCH, exactly like the route rule it sits beside: sign out of an
 * admin account and measurement resumes on the next `syncGaOptOut`. It is fed
 * from contexts/AuthContext.tsx, which is the only place that knows.
 *
 * Re-syncs the opt-out flag on a real change, because the interesting case is a
 * role that arrives AFTER the tag booted — an admin signing in mid-session. The
 * flag is the only thing that can silence gtag.js's own Enhanced Measurement
 * listeners once they are attached, so it has to move the moment we learn.
 *
 * NOT THE ONLY SOURCE, and deliberately so. AuthProvider is mounted by App.tsx,
 * i.e. only inside the SPA, and even there it resolves after `+Layout` has
 * already started the page-view tracker. So this is the AUTHORITATIVE source,
 * not the first one: `cachedViewerRole()` below answers synchronously from the
 * session this device already stored, on every route, before the first hit —
 * and is dropped the moment this function supplies a real answer.
 */
export function setGaViewerRole(role: GaViewerRole | null | undefined): void {
  const next: GaViewerRole = role ?? 'unknown';
  if (next === gaViewerRole) return;
  gaViewerRole = next;

  if (typeof window !== 'undefined') syncGaOptOut(window.location.pathname);
}

/**
 * GA4-03 (PR #702 review) — the role this device last signed in as, read
 * SYNCHRONOUSLY, with no round trip.
 *
 * WHY THIS IS NEEDED AT ALL. `setGaViewerRole` is fed from
 * contexts/AuthContext.tsx, which lives inside App.tsx — and App.tsx is
 * dynamically imported from an effect by four Vike pages and never mounted at
 * all by the rest (`/property/:slug`, `/for-sale/:type`, `/blog/:slug`,
 * `/guides/:slug`, `/about`…). So on a cold load of any of those, nothing ever
 * announced the role, it stayed `'unknown'`, and an administrator browsing the
 * public site was measured for the WHOLE VISIT — not merely for the first hit.
 * Even on the SPA routes, `+Layout` starts the page-view tracker before the
 * catch-all has finished importing App, so the first hit went out unlabelled.
 * Raising the opt-out flag afterwards cannot retract a hit already sent.
 *
 * The remedy is not to delay the page view until an auth round trip finishes —
 * that would hold up EVERY visitor's first hit, and lose it outright for anyone
 * who leaves in the meantime. It is to read what the app has already written
 * down. `afriqahome_user` is a plain localStorage blob, so this is one read and
 * one parse, and it is available before the tracker's first hit on every route.
 *
 * TRUST MODEL. This is a PROVISIONAL signal and it is only ever consulted while
 * no authoritative role has arrived; the moment `setGaViewerRole` supplies one,
 * this is never read again. hooks/useViewerRole.ts warns that the cached role
 * can be stale after a buyer↔agent correction (AGENT-22) — which is the right
 * caution for routing a visitor to a signup page, and the wrong one here: the
 * two ways this can be wrong are "exclude someone we needn't" (loses a little
 * data, safe) and "measure staff" (the thing being fixed). Nobody is promoted
 * to `admin` in this blob without signing in again, so the failure that matters
 * is not reachable through staleness.
 *
 * Returns null only when there is no `window` — never throws: a browser with
 * site data blocked, or a corrupt blob, reads as "we do not know".
 */
function cachedViewerRole(): GaViewerRole | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEYS.USER);
    if (!raw) return 'signed-out';

    const role = (JSON.parse(raw) as { role?: unknown } | null)?.role;
    return role === 'admin' || role === 'agent' || role === 'buyer' ? role : 'signed-out';
  } catch {
    return null;
  }
}

/**
 * True when the current viewer's role is one measurement must skip.
 *
 * Falls back to the cached session ONLY while no authoritative role has been
 * announced. Deliberately re-derived per call rather than latched into
 * `gaViewerRole`: leaving the module variable at `'unknown'` keeps "nobody has
 * told us" distinguishable from "we were told", which is what makes
 * `setGaViewerRole`'s first real answer authoritative rather than a no-op.
 */
export function isGaViewerExcluded(): boolean {
  const role = gaViewerRole === 'unknown' ? cachedViewerRole() ?? 'unknown' : gaViewerRole;
  return GA_EXCLUDED_VIEWER_ROLES.includes(role);
}

/**
 * Hold GA4's opt-out flag equal to "the current route is blocked".
 *
 * The second half of the blocked-route guarantee, and the half that covers a
 * blocked route reached from inside an already-measured session — see
 * `GA_DISABLE_FLAG`. It is called SYNCHRONOUSLY from the history patch below
 * rather than from the deferred page-view tracker: `pushState` has already
 * changed the address bar when it returns, so a scroll in the task between the
 * navigation and the tracker's `setTimeout(0)` would otherwise still be
 * measured against the blocked URL.
 *
 * Safe before the tag exists: this is a plain window property, and gtag.js reads
 * it whenever it boots.
 */
function syncGaOptOut(pathname: string): void {
  // GA4-03: with no measurement ID, `GA_DISABLE_FLAG` is the meaningless
  // `ga-disable-`. Writing it would put a stray global on every page of a build
  // that has no tag at all.
  if (!GA_ENABLED) return;
  if (typeof window === 'undefined') return;
  (window as unknown as Record<string, boolean>)[GA_DISABLE_FLAG] =
    isAnalyticsBlockedPath(pathname);

  // GA4-02 — consent can only ADD suppression to the line above, never remove
  // it. The route rule PR #699 shipped is untouched: blocked still means true,
  // whatever the visitor has agreed to.
  //
  // This half covers the one case the load-time gate cannot: a document where
  // the tag has ALREADY booted under a consent that has since been withdrawn —
  // "Cookie settings" then Decline, or a withdrawal in another tab. gtag.js
  // cannot be unloaded and its Enhanced Measurement listeners are not ours to
  // remove, so the opt-out flag is the only thing that still stops them. It is
  // re-asserted from every caller of this function, so a later navigation
  // cannot clear it back to the route-only answer while consent is still gone.
  if (!isAnalyticsConsentGranted()) {
    (window as unknown as Record<string, boolean>)[GA_DISABLE_FLAG] = true;
  }

  // GA4-03 — the third factor, and it only ever ADDS suppression too. Its own
  // block rather than a clause folded into the line above, so that the consent
  // rule and the staff-exclusion rule can be read, changed and tested apart.
  if (isGaViewerExcluded()) {
    (window as unknown as Record<string, boolean>)[GA_DISABLE_FLAG] = true;
  }
}


/**
 * GA4-04 — THE IDENTITY LAYER.
 *
 * WHAT WAS MISSING. Every hit this file sent was anonymous. GA4 stitched a
 * visit to a device and stopped there, so the three journeys the product is
 * actually built around were invisible: anonymous browsing → sign-up →
 * returning on another device was three unrelated users, and a buyer, an agent
 * and a member of staff were the same undifferentiated row in every report.
 *
 * WHAT THIS ADDS, and nothing more:
 *
 *   1. `user_id` — the Supabase user UUID, and ONLY ever that. Never an email,
 *      never a phone number, never a name. The shape is enforced rather than
 *      trusted (`GA_USER_ID_PATTERN`), on both the authoritative path and the
 *      provisional one, so a blob someone has hand-edited in localStorage
 *      cannot turn an address into a GA4 identifier.
 *   2. Four user properties — `user_role`, `account_stage`, `agent_tier`,
 *      `verification_status` — all closed enums, all derived, none of them a
 *      value a person typed.
 *   3. `journey_intent` — the pre-auth half, so a visitor who never signs in is
 *      still attributable to a surface. Set once per session from the LANDING
 *      url, and superseded by `user_role` the moment a real identity arrives.
 *
 * IT SENDS NOTHING. Identity reaches the tag through a `config` addressed to
 * the GA4 measurement id and carrying `send_page_view: false` — a dataLayer
 * command, not a hit. GA4-03 fixed a bug where a second consent grant re-sent
 * the make-good page view, and the shape that caused it was a `config` that
 * FIRED ONE; retiring `send_page_view` is what makes this one safe, and
 * pages/__tests__/gaGuard.test.ts pins that property directly rather than
 * counting configs. There is no `event` anywhere below.
 *
 * GA4-14 spoke 3b (D10) — WHY IT IS NO LONGER A GLOBAL COMMAND. It used to be
 * two global identity commands, which apply to EVERY configured destination.
 * With Google Ads configured beside GA4 that put a signed-in buyer's account id
 * on the Ads wire as `uid=`, verified in production on 2026-09-01. A `config`
 * is scoped to the destination it names; nothing else is.
 *
 * ALL THREE GATES STILL HOLD, because `applyGaIdentity` goes through
 * `canSendHit()` — the same predicate `sendGaEvent` uses. No measurement ID, a
 * blocked route, withheld consent or an excluded viewer and the identity sits
 * in module memory, unsent, until that stops being true. An administrator
 * therefore emits no identity at all: `isGaViewerExcluded()` refuses here for
 * the same reason it refuses a page view.
 *
 * COLD LOAD — THE ORDERING PROBLEM, AND THE ANSWER CHOSEN.
 *
 * The identity has to be on the tag BEFORE the first `page_view`, or the hit
 * that opens the session is the one hit that cannot be stitched. But the
 * authoritative answer comes from contexts/AuthContext.tsx, which is inside
 * App.tsx — dynamically imported from an effect by four Vike pages and never
 * mounted at all by the rest. That is precisely the ordering problem
 * `cachedViewerRole()` was written for, and this reuses its answer rather than
 * inventing a second one:
 *
 *   - `cachedGaIdentity()` reads the `afriqahome_user` blob SYNCHRONOUSLY, with
 *     no round trip, and stands in until an authoritative answer arrives.
 *   - `sendGaPageView` flushes identity immediately after the tag is booted and
 *     immediately BEFORE `gtag('event', 'page_view', …)`, so the first hit of
 *     the document already carries it.
 *
 * The alternative — folding `user_id` into `GA_CONFIG_PARAMS` — was rejected
 * twice over: that object is also serialized into the server-rendered bootstrap
 * in `src/pages/+Head.tsx`, where there is no session to read, so the two
 * bootstraps would drift; and refreshing identity mid-session would then mean a
 * second `config`, which is the double-page_view shape above.
 */

/** The two roles GA4 is allowed to know about. `admin` is not one of them. */
export type GaUserRole = 'buyer' | 'agent';

/** How far this account has walked the funnel. See `deriveGaAccountStage`. */
export type GaAccountStage = 'registered' | 'onboarded' | 'verified' | 'activated';

/** `profiles.tier`, lowercase as the column stores it. Agents only. */
export type GaAgentTier = 'ignite' | 'elevate' | 'dominate';

/** Agents only. Derived from `profiles.is_verified` — see `GaIdentity`. */
export type GaVerificationStatus = 'verified' | 'unverified';

/** What the LANDING surface says this visitor probably came for. */
export type GaJourneyIntent = 'buyer' | 'agent' | 'content' | 'unknown';

/**
 * Everything GA4 is told about who is signed in.
 *
 * `agentTier` and `verificationStatus` are agent-only by construction: they are
 * dropped for a buyer in `sanitizeGaIdentity` rather than merely left unset by
 * the caller, so a future call site cannot stamp a buyer with an agent property
 * by passing one.
 *
 * ON `verificationStatus` — A DEVIATION, STATED. The item names a
 * `verification_status` column. There is no such column: `public.profiles`
 * carries `is_verified boolean` and `verified_at timestamptz` and nothing else
 * (verified against the live schema, 2026-08-28). This is the boolean rendered
 * as the enum the item asked for; the property name and vocabulary are what
 * GA4-09 will register, and the day a real column appears only
 * `gaIdentityFromProfile` has to change.
 */
export interface GaIdentity {
  /** The Supabase auth user UUID. Anything that is not a UUID is refused. */
  userId: string;
  role: GaUserRole;
  accountStage?: GaAccountStage | null;
  /** Agents only. */
  agentTier?: GaAgentTier | null;
  /** Agents only. */
  verificationStatus?: GaVerificationStatus | null;
}

/**
 * The `profiles` columns the identity is derived from, in the shape PostgREST
 * returns them.
 *
 * Deliberately a loose structural type rather than `DbProfile`: this module is
 * imported by `src/pages/+Head.tsx` during SSR and evaluated directly by the
 * bare-`tsx` suites, and `lib/api/types.ts` drags `import.meta.env` into both.
 * The same reason `config/constants.ts` is the only import above.
 */
export interface GaProfileFacts {
  role?: string | null;
  tier?: string | null;
  is_verified?: boolean | null;
  subscription_status?: string | null;
  full_name?: string | null;
  phone?: string | null;
}

/**
 * The only shape a `user_id` may have.
 *
 * THE WHOLE PII RULE, AS CODE. "Send the UUID, never the email" is a convention
 * until something enforces it, and the value arrives here from two places that
 * can both be wrong — a localStorage blob any script on the origin can rewrite,
 * and a call site a future item adds. A non-UUID is refused outright rather
 * than sent and apologised for.
 */
export const GA_USER_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Every user property this module will ever write. Nothing else is settable. */
export const GA_USER_PROPERTY_KEYS = [
  'user_role',
  'account_stage',
  'agent_tier',
  'verification_status',
  'journey_intent',
] as const;

const GA_AGENT_TIERS: readonly GaAgentTier[] = ['ignite', 'elevate', 'dominate'];

/**
 * The subscription states that mean somebody is actually paying for this seat.
 *
 * `trial` is deliberately absent. ONBOARD-01 grants the three-month welcome
 * tier to every agent at signup, so 'trial' is the state of a brand-new account
 * rather than an activated one — counting it would make `account_stage` read
 * `activated` for essentially every agent on the property and measure nothing.
 */
export const GA_ACTIVATED_SUBSCRIPTION_STATUSES: readonly string[] = ['active', 'sponsored'];

/**
 * GA4-04 — the funnel ladder, highest rung reached.
 *
 * Derived from the `profiles` row alone: the item is read-only against that
 * table, and an identity that needed a second query would put a round trip in
 * front of the first page view of every signed-in visit.
 *
 *   activated  — an agent whose seat is genuinely being paid for (by them or by
 *                their agency). See GA_ACTIVATED_SUBSCRIPTION_STATUSES.
 *   verified   — `is_verified`. Agents only in practice; buyers are never
 *                verified, and the rung is left role-agnostic rather than
 *                special-cased so the boolean keeps meaning one thing.
 *   onboarded  — the profile carries what onboarding asks for: a name and a
 *                phone number, plus a tier for an agent.
 *   registered — the floor. An account exists.
 *
 * MONOTONIC, NOT EXCLUSIVE. Each rung means "reached at least this far", so an
 * unverified agent on a paid plan reads `activated` rather than falling back to
 * `onboarded`. Reading a stage as "and no further" is the one wrong way to use
 * this dimension.
 *
 * WHAT `activated` IS NOT, AND THIS IS THE HONEST GAP. The product's own
 * vocabulary for an activated agent is "has a live listing". That cannot be
 * answered from `profiles`, so this uses the closest column-level proxy — a
 * live commercial relationship — and the discrepancy is logged as a follow-up
 * rather than papered over.
 */
export function deriveGaAccountStage(
  role: GaUserRole,
  facts: GaProfileFacts | null | undefined,
): GaAccountStage {
  const nonEmpty = (value: unknown): boolean =>
    typeof value === 'string' && value.trim().length > 0;

  const status = nonEmpty(facts?.subscription_status)
    ? String(facts?.subscription_status).trim().toLowerCase()
    : '';

  if (role === 'agent' && GA_ACTIVATED_SUBSCRIPTION_STATUSES.includes(status)) return 'activated';
  if (facts?.is_verified === true) return 'verified';

  const hasProfileBasics = nonEmpty(facts?.full_name) && nonEmpty(facts?.phone);
  const hasTierIfAgent = role !== 'agent' || nonEmpty(facts?.tier);
  if (hasProfileBasics && hasTierIfAgent) return 'onboarded';

  return 'registered';
}

/**
 * GA4-04 — a `profiles` row plus a user id, as the one identity GA4 is told.
 *
 * The single place the mapping lives, so the authoritative call site in
 * contexts/AuthContext.tsx and the provisional cache read below cannot drift.
 *
 * Returns null — meaning "send no identity" — for anything that is not a
 * measurable signed-in visitor: a malformed id, an `admin` (whose exclusion is
 * enforced again at emission time), or a role outside the closed set.
 */
export function gaIdentityFromProfile(
  userId: string | null | undefined,
  facts: GaProfileFacts | null | undefined,
): GaIdentity | null {
  if (typeof userId !== 'string' || !GA_USER_ID_PATTERN.test(userId)) return null;

  const rawRole = typeof facts?.role === 'string' ? facts.role.trim().toLowerCase() : '';
  if (rawRole !== 'buyer' && rawRole !== 'agent') return null;
  const role: GaUserRole = rawRole;

  const rawTier = typeof facts?.tier === 'string' ? facts.tier.trim().toLowerCase() : '';
  const tier = (GA_AGENT_TIERS as readonly string[]).includes(rawTier)
    ? (rawTier as GaAgentTier)
    : null;

  return {
    userId,
    role,
    accountStage: deriveGaAccountStage(role, facts),
    agentTier: role === 'agent' ? tier : null,
    verificationStatus:
      role === 'agent' ? (facts?.is_verified === true ? 'verified' : 'unverified') : null,
  };
}

/**
 * Re-derive an identity handed in from outside, so the closed sets stay closed.
 *
 * Every field is re-checked rather than trusted, and the two agent-only
 * properties are dropped for a buyer HERE rather than at the call site — the
 * omission rule is a property of the identity, not a promise callers keep.
 */
function sanitizeGaIdentity(identity: GaIdentity | null | undefined): GaIdentity | null {
  if (!identity) return null;
  if (typeof identity.userId !== 'string' || !GA_USER_ID_PATTERN.test(identity.userId)) return null;
  if (identity.role !== 'buyer' && identity.role !== 'agent') return null;

  const stage = identity.accountStage;
  const tier = identity.agentTier;
  const verification = identity.verificationStatus;

  return {
    userId: identity.userId,
    role: identity.role,
    accountStage:
      stage === 'registered' || stage === 'onboarded' || stage === 'verified' || stage === 'activated'
        ? stage
        : null,
    agentTier:
      identity.role === 'agent' && typeof tier === 'string' && (GA_AGENT_TIERS as readonly string[]).includes(tier)
        ? (tier as GaAgentTier)
        : null,
    verificationStatus:
      identity.role === 'agent' && (verification === 'verified' || verification === 'unverified')
        ? verification
        : null,
  };
}

/**
 * GA4-04 — `journey_intent`, keyed on the surface the visitor LANDED on.
 *
 * WHY IT IS SESSION-SCOPED AND NOT PER-PAGE. The question it answers is "what
 * did this visit come for", and the only honest evidence for that is the
 * entry point. Recomputing it on every navigation would turn it into a second,
 * worse copy of `page_path`: an agent who lands on /join and then reads a guide
 * would be re-labelled `content` halfway through the visit they were recruited
 * into.
 */
export const GA_JOURNEY_INTENT_STORAGE_KEY = 'afriqahome_ga_journey_intent';

/**
 * The agent-side surfaces under `/agent`, spelled out.
 *
 * `/agent` is two different products sharing a first segment: the dashboard
 * (App.tsx:361, with `listings`/`analytics`/`settings`/`team` as child routes)
 * and the PUBLIC agent profile at `/agent/:slugOrId` (App.tsx:283), which is a
 * buyer surface — it is where a buyer reads about an agent before contacting
 * them. Shape cannot separate them, so the dashboard is enumerated and
 * everything else under `/agent` is read as a profile.
 */
const GA_AGENT_DASHBOARD_PATHS: readonly string[] = [
  '/agent',
  '/agent/listings',
  '/agent/analytics',
  '/agent/settings',
  '/agent/team',
  '/agent/new-listing',
];

/**
 * Landing surface → intent, first match wins.
 *
 * `exact` is the whole path; `prefixes` match the segment and anything below
 * it, so `/blog` covers `/blog/category/x`. Ordered agent → content → buyer
 * only for readability: the three sets are disjoint by construction.
 */
const GA_JOURNEY_INTENT_RULES: readonly {
  intent: GaJourneyIntent;
  exact: readonly string[];
  prefixes: readonly string[];
}[] = [
  {
    // Recruitment and agent-side work. `/join` is the evergreen agent landing
    // page (App.tsx:251), `/pricing` is the agent plan page, `/onboarding` is
    // AgentOnboarding behind an AgentRoute.
    intent: 'agent',
    exact: ['/onboarding', '/pricing', '/promo', '/agent-dashboard'],
    prefixes: ['/join'],
  },
  {
    // Reading rather than searching or selling. `/diaspora` is the one judgment
    // call in this set: its audience is diaspora BUYERS, but the page itself is
    // a hub of country guides whose every link is a `/guides/*` article
    // (src/pages/diaspora/+Page.tsx), so a visitor who lands there arrived to
    // read. Reclassifying it as `buyer` is a one-line change if the funnel
    // reporting says otherwise.
    intent: 'content',
    exact: ['/about', '/faq', '/contact', '/privacy', '/terms', '/syndication', '/diaspora'],
    prefixes: ['/blog', '/guides', '/careers'],
  },
  {
    // Property discovery. `/agents` is the agent DIRECTORY — a buyer looking
    // for someone to talk to — and is not `/agent`.
    intent: 'buyer',
    exact: ['/', '/agents', '/saved'],
    prefixes: ['/property', '/for-sale', '/syndicated', '/tools'],
  },
];

/**
 * What the landing URL says the visit is for.
 *
 * Pure and exported so the rules can be pinned without a browser. Returns
 * 'unknown' for anything unparseable, for the authenticated utility screens
 * (/messages, /profile, /account/*) that say nothing about intent, and for a
 * bare `/auth` — a sign-in page with no role in the query is the one surface
 * both journeys share, and guessing there would be worse than admitting it.
 */
export function deriveGaJourneyIntent(rawUrl: string | null | undefined): GaJourneyIntent {
  if (!rawUrl) return 'unknown';

  let url: URL;
  try {
    url = new URL(rawUrl, URL_PARSE_BASE);
  } catch {
    return 'unknown';
  }

  let path = url.pathname.toLowerCase();
  if (path.length > 1) path = path.replace(/\/+$/, '') || '/';

  // AUTH-DEEPLINK put the answer in the query: the LinkedIn agent ad lands on
  // `/auth?role=agent&mode=signup` (pages/Auth.tsx:61). Read as an enum, never
  // stored — the raw query never leaves this function.
  if (path === '/auth') {
    const role = url.searchParams.get('role');
    if (role === 'agent') return 'agent';
    if (role === 'buyer') return 'buyer';
    return 'unknown';
  }

  if (path === '/agent' || path.startsWith('/agent/')) {
    return GA_AGENT_DASHBOARD_PATHS.includes(path) || path.startsWith('/agent/edit-listing/')
      ? 'agent'
      : 'buyer';
  }

  for (const rule of GA_JOURNEY_INTENT_RULES) {
    if (rule.exact.includes(path)) return rule.intent;
    if (rule.prefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
      return rule.intent;
    }
  }

  return 'unknown';
}

let gaJourneyIntent: GaJourneyIntent | null = null;

function isGaJourneyIntent(value: unknown): value is GaJourneyIntent {
  return value === 'buyer' || value === 'agent' || value === 'content' || value === 'unknown';
}

/**
 * The intent for THIS session: the stored one if this tab already has one, the
 * landing URL's otherwise — and written down the first time so a reload, or a
 * cold navigation between two Vike pages, does not re-derive it from the second
 * surface.
 *
 * sessionStorage rather than localStorage: it is a per-visit claim, and a
 * `localStorage` copy would outlive the visit it describes and mislabel the
 * next one. Every access is wrapped — a browser with site data blocked reads as
 * "derive it again", never as a throw on the page-view path.
 */
function currentGaJourneyIntent(): GaJourneyIntent {
  if (gaJourneyIntent) return gaJourneyIntent;

  if (typeof window !== 'undefined') {
    try {
      const stored = window.sessionStorage?.getItem(GA_JOURNEY_INTENT_STORAGE_KEY);
      if (isGaJourneyIntent(stored)) {
        gaJourneyIntent = stored;
        return stored;
      }
    } catch {
      // Fall through and derive.
    }
  }

  const derived = deriveGaJourneyIntent(INITIAL_URL);
  gaJourneyIntent = derived;

  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage?.setItem(GA_JOURNEY_INTENT_STORAGE_KEY, derived);
    } catch {
      // A full or unavailable sessionStorage costs the cross-load stability of
      // this value and nothing else.
    }
  }

  return derived;
}

/** The authoritative identity, once contexts/AuthContext.tsx has supplied one. */
let gaIdentity: GaIdentity | null = null;

/**
 * Has an authoritative answer arrived at all?
 *
 * The same distinction `gaViewerRole`'s `'unknown'` draws: "nobody has told us"
 * is not "nobody is signed in". While this is false the cached blob stands in;
 * the moment it is true the cache is never read again — including when the
 * authoritative answer is `null`, which is what a sign-out looks like.
 */
let gaIdentityAnnounced = false;

/**
 * GA4-04 FIX #3 — downgrade a provisional identity that cannot answer the
 * derived questions, so it says nothing rather than guessing the floor.
 *
 * Pure and exported for the suite; the reasoning, and why `tier` is the
 * sentinel, is on `cachedGaIdentity` below. Applied ONLY to the cached blob —
 * the authoritative path in contexts/AuthContext.tsx reads the six columns
 * straight from `profiles`, where an absent `tier` is a fact about the account
 * rather than a gap in what this device happened to write down.
 */
export function provisionalGaIdentity(
  identity: GaIdentity | null | undefined,
): GaIdentity | null {
  if (!identity) return null;

  // A buyer's blob is not gated: `agentTier` is null for every buyer by
  // construction (`gaIdentityFromProfile`), so the sentinel says nothing about
  // whether their row was written in full, and buyers carry no agent property
  // that a partial blob could get wrong.
  if (identity.role !== 'agent') return identity;
  if (identity.agentTier !== null) return identity;

  return {
    userId: identity.userId,
    role: identity.role,
    accountStage: null,
    agentTier: null,
    verificationStatus: null,
  };
}

/**
 * GA4-04 FIX #3 — a PROVISIONAL identity may assert who the visitor is, never
 * how far they have got.
 *
 * THE BUG. Reported from production DebugView for an agent whose `profiles` row
 * reads `subscription_status = 'sponsored'`: the hit carried
 * `account_stage: 'registered'` (the floor, two rungs low) and NO `agent_tier`
 * key at all. Neither `deriveGaAccountStage` nor the emission gate was wrong —
 * fed the real row both answer `activated` / `elevate`. What was wrong is that
 * they were fed the CACHED BLOB, and that blob does not always carry the
 * columns they read.
 *
 * Every failure mode of the authoritative path in contexts/AuthContext.tsx
 * (:502-:565) ends in `setGaIdentity(null)`, which clears the identity
 * outright — so a hit that still carries `user_role` proves the authoritative
 * answer had not LANDED yet, rather than that it failed. The window is real and
 * it is the common case: the tracker's own page view (:1823, :1932) fires as
 * soon as the tag boots, ahead of `isLoading` resolving and ahead of two
 * network round trips.
 *
 * THE INVARIANT THIS BLOCK USED TO CLAIM, AND WHY IT WAS FALSE. It said
 * `dbProfileToUser` writes the blob "from a real `profiles` row, so the fields
 * are the right ones", and that the only risk was staleness. Three ways that is
 * untrue, all of them live:
 *
 *   1. Two writers never reach `dbProfileToUser` at all. contexts/AuthContext
 *      .tsx:1088 (agent signup, before AgentOnboarding creates the row) and
 *      :1145 (login with no profile) write a five-key object literal — id,
 *      email, role, name, avatarUrl. No tier, no subscriptionStatus, no
 *      isVerified, no phone.
 *   2. `dbProfileToUser` itself drops keys. It maps `tier: profile.tier ||
 *      undefined` (lib/api/types.ts:532), and `JSON.stringify` OMITS undefined
 *      — so a null column leaves no key behind, indistinguishable from (1).
 *   3. `checkSubscriptionStatus` (contexts/AuthContext.tsx:255) rewrites the
 *      status before the blob is written: a sponsored seat whose
 *      `subscription_expires_at` has passed is stored as `expired`.
 *
 * So the blob's fields are not "the right ones, possibly stale". They may be
 * ABSENT, and absence reads as a fact here: a missing `subscription_status`
 * becomes `''` and fails the activated test, a missing `phone` fails
 * `hasProfileBasics`, and `is_verified` is coerced to `false` rather than
 * unknown. The floor is what a half-written blob always derives.
 *
 * THE RULE. `tier` is the sentinel. Every complete writer carries it for an
 * agent, and none of the partial ones do — so an agent blob with no recognised
 * tier is not a low-stage agent, it is an UNRESOLVED one, and this returns an
 * identity that says so by withholding all three derived properties.
 *
 * WHY ROLE-ONLY AND NOT `null`. Returning nothing would take `user_id` off the
 * first page view of every affected visit, and stitching that hit to the signed
 * -in user is the whole reason this function exists — the guarantee PR #703's
 * `beginGaIdentityFor` and PR #704's three-state `user_id` were built to
 * protect. `userId` and `role` are the two fields EVERY writer sets and neither
 * is derived, so they stay. Withholding is also self-healing in the one
 * direction that matters: `applyGaIdentity` adds the three keys when the
 * authoritative answer lands, which needs no retraction, whereas overwriting
 * `registered` with `activated` leaves the wrong value on every hit in between.
 *
 * WHAT IS STILL PROVISIONAL AND STILL ACCEPTED. `full_name` is read from the
 * blob's `name`, which `dbProfileToUser` defaults to the literal 'User', so a
 * nameless account can read one rung high (`onboarded`) until the authoritative
 * answer lands. And (3) above still applies to a COMPLETE blob: a lapsed
 * sponsored seat can read one rung low. Both are bounded one-rung errors on a
 * property that is present and then corrected — not a missing dimension key —
 * and the alternative remains holding the first page view of every signed-in
 * visit behind an auth round trip. The two blob-writing defects themselves
 * ((1)/(2) and (3)) are tracked separately; this function defends against them
 * rather than fixing them.
 */
function cachedGaIdentity(): GaIdentity | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEYS.USER);
    if (!raw) return null;

    const blob = JSON.parse(raw) as {
      id?: unknown;
      role?: unknown;
      tier?: unknown;
      isVerified?: unknown;
      subscriptionStatus?: unknown;
      name?: unknown;
      phone?: unknown;
    } | null;

    return provisionalGaIdentity(
      gaIdentityFromProfile(typeof blob?.id === 'string' ? blob.id : null, {
        role: typeof blob?.role === 'string' ? blob.role : null,
        tier: typeof blob?.tier === 'string' ? blob.tier : null,
        is_verified: blob?.isVerified === true,
        subscription_status:
          typeof blob?.subscriptionStatus === 'string' ? blob.subscriptionStatus : null,
        full_name: typeof blob?.name === 'string' ? blob.name : null,
        phone: typeof blob?.phone === 'string' ? blob.phone : null,
      }),
    );
  } catch {
    return null;
  }
}

/** The identity in force: the authoritative one if we have been told, else the cache. */
function effectiveGaIdentity(): GaIdentity | null {
  return gaIdentityAnnounced ? gaIdentity : cachedGaIdentity();
}

/**
 * The user properties as they should stand right now — PRESENT KEYS ONLY.
 *
 * Omission is the contract, not a null: a buyer's payload has no `agent_tier`
 * KEY at all, which is what the item asks for and what keeps a GA4 report from
 * growing an "(not set)" row for a dimension that does not apply. Anything that
 * was set before and is absent here is explicitly nulled by `applyGaIdentity`,
 * so omission can never leave a stale value on the tag either.
 */
function gaUserProperties(): Record<string, string> {
  const identity = effectiveGaIdentity();

  // GA4-04 FIX #2 — `journey_intent` IS SESSION-SCOPED, NOT IDENTITY-SCOPED,
  // so it is always present and is never retracted.
  //
  // It used to be dropped the moment an identity arrived, which put
  // `journey_intent: null` into the retraction pass below. Reported from
  // production: after signing in, DebugView showed `journey_intent` still
  // listed, expanding to a BLANK value — the same present-but-empty signature
  // PR #704 fixed for `user_id`.
  //
  // WHY DROPPING IT CANNOT WORK, measured rather than assumed. User properties
  // PERSIST across hits until overwritten; they are not a per-hit snapshot.
  // pages/__tests__/fixtures/gaIdentityLifecycleProbe.ts shows the second page
  // view of a signed-in session emitting ZERO `set` commands while still
  // sending a `page_view` — so anything visible on that hit was retained, not
  // re-sent. Persistence is value-agnostic: a `null` is retained exactly as
  // stickily as a string. So the retraction never was a one-hit cost that
  // could be minimised; it was a permanent blank row for the rest of the
  // session, and no version of "null it, but only once" avoids that.
  //
  // WHY THERE WAS NOTHING TO RETRACT ANYWAY. The other four properties describe
  // WHO THE VISITOR IS. When one stops applying it must be retracted, because
  // keeping the stale value is a false claim about whoever is using the device
  // now — a blank is the honest "we no longer know", and that is the same
  // conclusion #704 reached for `user_id` on sign-out. `journey_intent`
  // describes HOW THIS SESSION STARTED. That does not stop being true when
  // somebody signs in. Classing it with the identity properties was the bug.
  //
  // The item's "superseded by real role after login" still holds, and is
  // satisfied the only way it ever could be: `user_role` is present and
  // authoritative. Supersession by precedence, not by erasure — erasure is not
  // a thing GA4 offers. Keeping both is also what makes the funnel legible:
  // "landed on /join, signed in as a buyer" needs the two on the same user.
  const properties: Record<string, string> = { journey_intent: currentGaJourneyIntent() };

  if (!identity) return properties;

  properties.user_role = identity.role;
  if (identity.accountStage) properties.account_stage = identity.accountStage;
  if (identity.role === 'agent') {
    if (identity.agentTier) properties.agent_tier = identity.agentTier;
    if (identity.verificationStatus) properties.verification_status = identity.verificationStatus;
  }
  return properties;
}

/** What was last handed to the tag, so a re-flush that changes nothing is silent. */
let lastAppliedIdentitySignature: string | null = null;
let lastAppliedUserProperties: Record<string, string> = {};

/**
 * GA4-04 FIX — the `user_id` this tag has been told about, in THREE states.
 *
 *   a UUID    — a real identity is on the tag.
 *   `null`    — one was on the tag and has been explicitly retracted.
 *   `undefined` — this tag has never been told about a user at all.
 *
 * The third state is the whole point, and it is the one the shipped code did
 * not have. `null` and "never mentioned" are different things to gtag: a
 * `set` with a null value still REGISTERS the field, which is why a pre-auth
 * page view surfaced a present-but-blank `user_id` row in DebugView rather
 * than no row at all. See `applyGaIdentity`.
 */
let lastAppliedUserId: string | null | undefined;

/**
 * Push the current identity onto the tag — and NOTHING ELSE.
 *
 * `canSendHit()` is the gate, deliberately the same one `sendGaEvent` uses, so
 * the three factors this file has accumulated all apply unchanged: no
 * measurement ID, a blocked route, withheld consent or an excluded viewer and
 * this returns having emitted nothing. Identity is not queued and not retried;
 * it simply sits in module memory until the next flush finds the gates open,
 * which is what makes "consent granted later" work without a second code path.
 *
 * IDEMPOTENT. The tracker flushes on every page view, and re-announcing the
 * same identity would put a redundant pair of commands on the dataLayer for
 * every navigation of every signed-in visit.
 */
type GaIdentityConfig = {
  /**
   * Always present, always `false`, spread from `GA_CONFIG_PARAMS`. It is the
   * whole reason a `config` may carry identity without reintroducing GA4-03's
   * double page view, so it is in the type rather than left to the caller.
   */
  send_page_view?: boolean;
  /** `null` is gtag's UNSET, which is what a sign-out writes. */
  user_id?: string | null;
  user_properties?: Record<string, string | null>;
  /** Case-page referrer: see `documentReferrerConfig`. */
  page_referrer?: string;
};

function applyGaIdentity(): void {
  if (!canSendHit()) return;

  const identity = effectiveGaIdentity();
  const next = gaUserProperties();
  const userId = identity?.userId ?? null;

  // KEYED ON THE DESIRED STATE, not on the payload built below. The payload also
  // carries the explicit nulls for keys that have just been dropped, and those
  // describe the TRANSITION rather than the state — so including them would make
  // two consecutive flushes of one unchanged identity hash differently, and the
  // tracker flushes on every page view.
  const signature = JSON.stringify([userId, next]);
  if (signature === lastAppliedIdentitySignature) return;

  // Explicitly unset anything that was set and no longer applies — a sign-out,
  // or an agent property that must not survive a change of role. `null` is
  // gtag's documented "forget this user property"; omitting the key would
  // leave the old value on the tag for the rest of the document.
  const properties: Record<string, string | null> = { ...next };
  for (const key of Object.keys(lastAppliedUserProperties)) {
    if (!(key in next)) properties[key] = null;
  }

  lastAppliedIdentitySignature = signature;
  lastAppliedUserProperties = next;

  // GA4-04 FIX — SAY NOTHING ABOUT A USER WE HAVE NEVER HAD.
  //
  // This used to be written unconditionally, so a genuinely anonymous visitor —
  // fresh incognito window, no cached session, nobody announced — had a
  // `user_id` of `null` pushed onto the tag before their first
  // page view. Verified in production DebugView on 2026-08-29: the `page_view`
  // carried a `user_id` row that expanded to a BLANK value rather than reading
  // `(not set)`, which is GA4 saying the field was present and empty rather
  // than absent. `null` is gtag's documented way to UNSET a field, and
  // unsetting a field that was never set is not a no-op — it registers it.
  //
  // So there are three cases and only two of them may speak:
  //   * a real id  — write it.
  //   * an id we previously wrote, now gone (sign-out, or the handover
  //     `beginGaIdentityFor` performs) — write `null`, which is the only way
  //     to retract it; a booted tag cannot have a field removed.
  //   * never had one — write nothing, so the field never exists.
  //
  // The value check is `GA_USER_ID_PATTERN`, the same control the rest of this
  // block enforces, rather than a second and looser "is it truthy" — an empty
  // string, an email or a truncated uuid must all fail the same way here as
  // they do in `gaIdentityFromProfile`.
  const hasUserId = typeof userId === 'string' && GA_USER_ID_PATTERN.test(userId);

  // GA4-14 spoke 3b (D10) — IDENTITY IS CONFIG-SCOPED, NEVER GLOBAL.
  //
  // THE PRODUCTION FAILURE. This used to be two global identity commands — one
  // for `user_id`, one for `user_properties`. A global command applies to every
  // hit on every CONFIGURED DESTINATION, and spoke 3 added a second one — a
  // `config` for the Google Ads container. Verified in production on
  // 2026-09-01: `google.com/ccm/collect?tid=AW-…&…&uid=11cb8b16-…` — a signed-in
  // buyer's account id, on the Google Ads wire, from a command written for GA4.
  // `set` has no `send_to`; there is no such thing as a scoped `set`.
  //
  // A `config` for the GA4 measurement id is scoped to that destination by
  // construction, and `user_id` / `user_properties` are both documented fields
  // of it. Nothing addressed to `GA_MEASUREMENT_ID` can reach an advertising
  // destination — and since spoke 3c there is no advertising destination on
  // this tag at all, which makes the scoping a second fence rather than the
  // only one.
  //
  // WHY THIS DOES NOT REINTRODUCE GA4-03's DOUBLE PAGE VIEW. That bug was a
  // `config` that FIRED A HIT. `GA_CONFIG_PARAMS` is `{ send_page_view: false }`
  // and is spread first here, so this config cannot send one — which is the
  // property the tests now assert, in place of the weaker "config runs exactly
  // once" that this change necessarily retires. Exactly one `page_view` still
  // leaves the document, and that is asserted directly.
  //
  // SAY NOTHING ABOUT A USER WE HAVE NEVER HAD (GA4-04). The three cases are
  // unchanged: a real id is written; an id we previously wrote and have lost is
  // retracted with `null`; one we never had is not mentioned at all, because
  // `null` is gtag's UNSET and unsetting a field that was never set registers
  // it. So the payload is assembled conditionally and an empty one is not sent.
  const identityConfig: GaIdentityConfig = { ...GA_CONFIG_PARAMS, ...documentReferrerConfig() };
  let hasSomethingToSay = false;

  if (hasUserId || lastAppliedUserId != null) {
    identityConfig.user_id = hasUserId ? userId : null;
    lastAppliedUserId = hasUserId ? userId : null;
    hasSomethingToSay = true;
  }

  // The same rule for the properties. Unreachable today with an empty payload —
  // an empty one is always a repeat and the signature above catches those — but
  // stated rather than left as an accident of the dedupe, because "emit only
  // what you actually have to say" is the invariant this fix is about.
  if (Object.keys(properties).length > 0) {
    identityConfig.user_properties = properties;
    hasSomethingToSay = true;
  }

  if (!hasSomethingToSay) return;

  window.gtag('config', GA_MEASUREMENT_ID, identityConfig);
}

/**
 * GA4-04 — the one entry point. Fed from contexts/AuthContext.tsx, beside the
 * `setGaViewerRole` call that has fed the exclusion flag since GA4-03.
 *
 * `null` means "no measurable identity": signed out, an administrator, or a
 * session we could not validate. It is an authoritative answer like any other
 * — it retires the cached blob and clears whatever is on the tag.
 *
 * Safe to call before consent, before the tag boots, and during SSR. Nothing
 * below reaches gtag unless `applyGaIdentity`'s gate says it may.
 */
export function setGaIdentity(identity: GaIdentity | null | undefined): void {
  const next = sanitizeGaIdentity(identity);

  gaIdentityAnnounced = true;
  gaIdentity = next;

  applyGaIdentity();
}

/**
 * GA4-04 (PR #703 review, P1) — a resolution for `userId` is STARTING; retire
 * anything in force that is not theirs, synchronously, right now.
 *
 * THE WINDOW THIS CLOSES. `setGaIdentity` is the END of an async resolution —
 * a token check and a profile read. When one signed-in account is replaced
 * directly by another without an intervening sign-out (the cross-tab
 * `SIGNED_IN` path `sessionEpoch` above exists for), the previous account's
 * identity stays on the tag for the whole of that round trip, and every hit in
 * the gap is stamped with the WRONG user's UUID. A slow network makes the gap
 * arbitrarily long. Attributing one person's browsing to another person's id is
 * the one failure this layer must not have, so the correction is synchronous
 * and it happens before the first `await`.
 *
 * IT IS NOT A BLANKET CLEAR, and that distinction is the cold-load guarantee.
 * Re-resolving the SAME user leaves the identity exactly where it is — which is
 * what keeps the provisional answer `cachedGaIdentity()` supplies standing
 * through the first page view of a restored session, instead of retracting it
 * for the duration of a round trip that is about to confirm it.
 *
 * A `null`/absent `userId` clears too: "resolving for nobody" is a sign-out.
 */
export function beginGaIdentityFor(userId: string | null | undefined): void {
  const current = effectiveGaIdentity();
  // Nothing in force — including the pre-auth state, whose `journey_intent`
  // must survive this untouched.
  if (!current) return;
  if (typeof userId === 'string' && current.userId === userId) return;

  setGaIdentity(null);
}

/**
 * Is it worth resolving an identity at all?
 *
 * Read by contexts/AuthContext.tsx BEFORE it spends a request on the profile
 * read. Two stable factors only — a build with no measurement ID measures
 * nothing anywhere, and an excluded viewer must never be identified however the
 * rest of the session goes. Consent is deliberately NOT one of them: it can be
 * granted later in the visit, and an identity resolved in advance is what lets
 * that grant carry `user_id` on its very first hit rather than on the one
 * after. Nothing is emitted in the meantime — `applyGaIdentity` still refuses.
 */
export function isGaIdentityCollectable(): boolean {
  return GA_ENABLED && !isGaViewerExcluded();
}

/** The sanitized `page_location` for the page currently on screen. */
function currentAnalyticsUrl(): string {
  if (typeof window === 'undefined') return '';
  // GA4-03: `reportableUrl`, not the bare sanitizer. An event fired on
  // /messages/<uuid> must stamp the same `page_location` the page view for that
  // screen carried, or the two describe different pages in every report that
  // joins them.
  return reportableUrl(window.location.href);
}

/**
 * True when a hit may be sent right now: we are in a browser, the tag has
 * booted, and the current route is not blocked.
 *
 * GA4-13 deliberately does NOT boot the tag from here, only from the page-view
 * path. Two reasons. The Capacitor build has no bootstrap at all — its entry is
 * index.native.html → index.tsx → App.tsx, which renders neither `+Head.tsx` nor
 * `+Layout.tsx` — so `window.gtag` has never existed in the app and every event
 * has always been a no-op there. Booting from here would quietly switch mobile
 * analytics on, which is not this item's call to make. And it would buy nothing:
 * every `sendGaEvent` call site in the app is a user action (a sign-up, a share,
 * a saved listing), so by the time one runs the page view for that screen has
 * long since booted the tag.
 */
function canSendHit(): boolean {
  if (!GA_ENABLED) return false;
  if (typeof window === 'undefined') return false;
  if (typeof window.gtag !== 'function') return false;
  // GA4-03: staff traffic. The opt-out flag would swallow the hit anyway, but
  // refusing here keeps it off the dataLayer entirely rather than relying on
  // gtag.js to discard it after the fact.
  if (isGaViewerExcluded()) return false;
  // GA4-02: consent is checked here as well as at boot, because the tag can
  // outlive the consent that booted it — see `syncGaOptOut`. Without this line
  // `sendGaEvent` would keep pushing events onto a booted-but-withdrawn tag and
  // rely entirely on the opt-out flag to swallow them.
  if (!isAnalyticsConsentGranted()) return false;
  return !isAnalyticsBlockedPath(window.location.pathname);
}

/**
 * GA4-14 spoke 3b (D10) — THE ONLY `gtag('event', …)` IN THE APPLICATION.
 *
 * WHAT WENT WRONG. `gtag('event', name, params)` WITHOUT `send_to` is delivered
 * to EVERY destination the document has configured. That was harmless while the
 * GA4 property was the only one; the moment spoke 3 added a `config` for the
 * Google Ads container, every GA4 event started arriving at Google Ads
 * as well. Verified in production on 2026-09-01: a `page_view` for `/messages`
 * — a route no advertising tag may run on — and a `search` carrying
 * `ep.search_term=Westlands` both landed on `google.com/ccm/collect` under
 * `tid=AW-18405568949`, in flat contradiction of Privacy Policy v3 §4.5 and
 * §2.5. No app code was wrong at either call site; the routing was.
 *
 * So there is exactly one place in the app that may write `gtag('event'`, and
 * it cannot be called without naming a destination. `pages/__tests__` pins that
 * at the source level, repo-wide, so a new call site cannot reintroduce a
 * broadcast by being written the obvious way.
 *
 * `send_to` is spread LAST, after the caller's params, so no call site can
 * redirect its own event by passing one of its own.
 */
function sendGtagEvent(
  destinationId: string,
  eventName: string,
  params: Record<string, unknown>,
): void {
  window.gtag('event', eventName, { ...params, send_to: destinationId });
}

/**
 * GA4-14 spoke 3b (D10) — a GA4 event, addressed to the GA4 property alone.
 *
 * The helper every analytics call site goes through, and — since GA4-14 spoke
 * 3c removed the advertising destination from this tag — the ONLY caller of
 * `sendGtagEvent` there is. `send_to` is therefore addressed to the same
 * property that is the only one configured, which reads like a tautology and is
 * not one: it is the fence that makes adding a second destination a deliberate
 * act rather than a silent broadcast, and pages/__tests__ pins it as such.
 */
function gaEvent(eventName: string, params: Record<string, unknown>): void {
  sendGtagEvent(GA_MEASUREMENT_ID, eventName, params);
}

/**
 * Send a GA4 event through the guard.
 *
 * Every GA4 hit carries `page_location`, so the sanitized URL is stamped on
 * events too — not just page views. Without it a `view_item` fired on a URL
 * with a sensitive param would leak exactly what R3 is meant to prevent.
 *
 * GA4-14 spoke 3b (D10): it addresses the GA4 property explicitly, through
 * `gaEvent` — see the two functions above.
 */
export function sendGaEvent(eventName: string, params?: Record<string, unknown>): void {
  if (!canSendHit()) return;

  gaEvent(eventName, {
    ...(params as Record<string, string | number | boolean | undefined>),
    page_location: currentAnalyticsUrl(),
  });
}

/** The path portion of a URL, for the route guard and de-duplication. */
function pathOf(url: string): string {
  try {
    return new URL(url, URL_PARSE_BASE).pathname;
  } catch {
    return '';
  }
}

/**
 * Send one sanitized `page_view` for `url`.
 *
 * The route guard runs against the URL BEING REPORTED rather than against
 * `window.location`, so a hit can never describe a blocked route even if the
 * app has already navigated on.
 *
 * `page_referrer` is set explicitly because gtag otherwise fills it from the
 * raw previous URL — which, for the redirect out of `/auth/callback`, is the
 * token URL itself.
 *
 * GA4-13: this is also where the tag gets booted when the server-rendered
 * bootstrap was skipped, which is why the route guard runs before it and not
 * after.
 */
function sendGaPageView(url: string, referrer: string): void {
  if (typeof window === 'undefined') return;
  if (!GA_ENABLED) return;

  // GA4-03 — THE RAW PATH, and before anything else touches the URL. The
  // blocklist is a security control and the normalizer is a reporting nicety;
  // running the normalizer first would mean deciding whether a route is
  // blocked by looking at a string this module invented. `/messages` and
  // `/join` are not blocked today, but the ordering is what keeps that a
  // product decision rather than an accident of rule overlap.
  if (isAnalyticsBlockedPath(pathOf(url))) return;

  // GA4-03: staff. Checked here and not only in `canSendHit`, because page
  // views do not go through that predicate — and because this is the call that
  // would otherwise BOOT the tag for an administrator.
  if (isGaViewerExcluded()) return;

  // GA4-03 — CONSENT, CHECKED HERE AND NOT ONLY AT THE BOOTSTRAP.
  //
  // `ensureGtagBootstrapped` below does check it, but it short-circuits on
  // `typeof window.gtag === 'function'` FIRST — so on a document where the tag
  // has already booted and consent has since been withdrawn ("Cookie settings"
  // then Decline, or a withdrawal in another tab) it returns true without
  // consulting consent at all, and the page view was pushed onto a tag that
  // gtag.js then silently discarded via the opt-out flag.
  //
  // That was harmless until GA4-03, because nothing recorded it. It is not
  // harmless now: `lastSentPath` below would record a discarded hit as proof
  // the screen had been measured, and the make-good page view owed when the
  // visitor accepts again on that same screen would be suppressed — losing the
  // page view entirely rather than double-counting it.
  //
  // The same predicate `canSendHit` applies to events, for the same reason.
  if (!isAnalyticsConsentGranted()) return;

  const pageLocation = reportableUrl(url);
  if (!pageLocation) return;

  // GA4-13: last, and only once both route checks have passed — this is what
  // injects gtag.js when the server-rendered bootstrap was skipped. It runs the
  // blocked-path check again against the live `window.location`, so the tag
  // cannot boot while the browser is still standing on a blocked route.
  if (!ensureGtagBootstrapped()) return;

  // GA4-04 — IDENTITY BEFORE THE HIT, and this one line is the cold-load answer.
  //
  // The tag has just booted (or was already booted) and no hit has left yet, so
  // this is the last moment at which the FIRST page_view of the document can be
  // made to carry `user_id`. A GA4-scoped `config` applies to every subsequent
  // hit for that destination, so the event three lines below already has it —
  // and it carries `send_page_view: false`, so it re-sends nothing. On a cold
  // load the value comes from
  // `cachedGaIdentity()` synchronously; contexts/AuthContext.tsx overwrites it
  // with the authoritative answer a moment later, and `applyGaIdentity` is
  // idempotent, so that costs nothing when the two agree.
  applyGaIdentity();

  const params: Record<string, string> = { page_location: pageLocation };

  // GA4-03 — `page_path` is the normalized path ALONE, no query.
  //
  // READ THIS BEFORE RELYING ON IT IN A REPORT. `page_path` is a Universal
  // Analytics field, not a GA4 one: GA4 derives its own page dimension from
  // `page_location`, and gtag forwards this as an ordinary custom event
  // parameter (`ep.page_path` on the wire). It will not appear in a report
  // until it is registered as a custom dimension in the GA4 console, which is
  // GA4-09's job. It is sent anyway because it is the exact value the item
  // asked for and because it is the cheapest thing to register later.
  //
  // The high-cardinality fix does NOT depend on it. That comes from
  // `page_location` above, which is the field GA4 actually builds the page
  // report from — and which is normalized identically.
  //
  // Read straight back off the URL we just built rather than recomputed, so
  // the two parameters cannot describe different pages. Query-free on purpose:
  // `page_location` already carries the campaign params.
  params.page_path = normalizeAnalyticsPath(pathOf(pageLocation)) || '/';

  // GA4-03: the referrer is normalized too. It is an in-app URL on every
  // navigation after the first, so leaving it raw would hand GA4 the same
  // `/messages/<uuid>` the page_path was just collapsed to hide — one screen
  // later, and through a parameter nobody thinks to look at.
  const sanitizedReferrer = reportableUrl(referrer);
  // GA4-03: never report a page as its own referrer. The make-good hit uses the
  // last URL we reported, and after "away and back" that is this very page —
  // a self-referral, which GA4 reads as a real navigation from here to here.
  // Omitting the parameter says "unknown", which is the truth.
  //
  // Case-page referrer: in gtag.js an omitted `page_referrer` is filled, not
  // left empty. It used to be filled from the raw `document.referrer`; it is now
  // filled from the config's value (`documentReferrerConfig`), which has
  // already been through `reportableUrl`.
  if (sanitizedReferrer && sanitizedReferrer !== pageLocation) {
    params.page_referrer = sanitizedReferrer;
  }

  // GA4-14 spoke 3b (D10): addressed to the GA4 property. Without `send_to`
  // this hit was broadcast to Google Ads too — the P4 production leak, where a
  // `page_view` for the private `/messages` route reached `AW-18405568949`.
  gaEvent('page_view', params);

  lastSentUrl = url;

  // GA4-03: recorded AFTER the send and keyed on the RAW path, so it means
  // exactly "a page view for this path reached the tag". `lastTrackedPath`
  // above cannot answer that — it records paths that were considered and then
  // refused. See `sendConsentedPageView`.
  lastSentPath = pathOf(url);
}

/**
 * GA4-02 — subscribers to "the browser is now on a different path".
 *
 * The consent banner needs this. It must not render on a transient or blocked
 * surface (/auth/callback, the password-reset route, /admin*), and every exit
 * from those is a client-side navigation in this app — see the routing note on
 * `ensureGtagBootstrapped`. Without a signal, a visitor who lands on the email
 * verification screen would never be asked for the rest of the visit.
 *
 * It is published from here rather than patched a second time in the banner
 * because `startGaPageviewTracking` below already owns the one patch on
 * `history.pushState`/`replaceState`, and two components wrapping the same
 * method cannot be unwound in an arbitrary order without leaving one installed
 * forever.
 */
/**
 * WHICH history operation produced a route change.
 *
 * GA4-14 spoke 3 added this argument, and it carries a distinction that turns
 * out to matter a great deal to the ad layer:
 *
 *   'push' / 'pop' — the visitor NAVIGATED. A new screen.
 *   'replace'      — the APP corrected the address bar for the screen that is
 *                    already there. Vike strips the query on hydration
 *                    (`/?utm_source=x` becomes `/`), pages/Home.tsx:526 writes
 *                    the filter state into it, and a `<Navigate replace>`
 *                    redirect like /join/<code> → /join is one of these too.
 *
 * Analytics does not care: its dedupe is by path and a replace that changes the
 * path is a real screen change. Advertising does, because the refusals it makes
 * are about the URL the visitor ARRIVED with — and a replace that arrives a
 * moment later, with the offending query already removed, would otherwise erase
 * the reason the page view was refused. See `startAdTagTracking` in
 * utils/adTags.ts.
 */
export type GaRouteChangeKind = 'push' | 'replace' | 'pop';

const routeChangeListeners = new Set<(kind: GaRouteChangeKind) => void>();

/**
 * Subscribers may ignore the argument — src/components/ConsentBanner.tsx does,
 * because "am I on a blocked route" is the same question however you got here.
 */
export function onGaRouteChange(listener: (kind: GaRouteChangeKind) => void): () => void {
  routeChangeListeners.add(listener);
  return () => {
    routeChangeListeners.delete(listener);
  };
}

function notifyGaRouteChange(kind: GaRouteChangeKind): void {
  for (const listener of routeChangeListeners) listener(kind);
}

/**
 * The last path a page view was CONSIDERED for — sent or suppressed.
 *
 * Module-level rather than per-subscription so a remount (React StrictMode
 * double-invokes effects) cannot re-send the initial view. Suppressed paths are
 * recorded too: without that, `/` → `/admin` → `/` would see the same path it
 * last sent and drop the return visit.
 */
let lastTrackedPath: string | null = null;

/**
 * GA4-03 — the last path a page view was actually SENT for.
 *
 * Distinct from `lastTrackedPath`, which records what the tracker CONSIDERED.
 * The difference is the whole of the make-good rule: before consent every path
 * is considered and none is sent, so "considered" cannot tell you whether the
 * screen in front of the visitor has been measured yet. Only this can.
 *
 * Module-level for the same reason as `lastTrackedPath`: it has to outlive a
 * StrictMode remount of the tracker.
 */
let lastSentPath: string | null = null;

/**
 * GA4-03 — the URL of that same hit, kept as the honest referrer for a later
 * make-good.
 *
 * `sendConsentedPageView` used `INITIAL_REFERRER` unconditionally, on the stated
 * grounds that a grant is "by construction the FIRST hit of the session". A
 * repeat grant is not: "Cookie settings" then Accept on a later screen would
 * report the visit's original EXTERNAL referrer again, mid-session, which reads
 * to GA4 as a fresh acquisition from that source.
 */
let lastSentUrl: string | null = null;

/**
 * Start sending sanitized page views: one for the page this document opened
 * with, then one per client-side route change.
 *
 * WHY IT PATCHES `history`. The app has two client routers — Vike's between
 * server-rendered pages, React Router's inside src/pages/[[catchAll]] — and
 * this runs in `+Layout`, above both. `pushState`/`replaceState` plus
 * `popstate` is the one signal both of them produce.
 *
 * De-duplication is by PATH, not by full URL. The routers emit
 * replaceState-then-pushState pairs for a single navigation, and both of them
 * rewrite the address bar after landing (see INITIAL_URL); keying on the path
 * counts each of those once. Query and fragment cannot distinguish two pages
 * here anyway — the sanitizer has already reduced them to the campaign params.
 *
 * Returns a cleanup function that restores the patched history methods.
 */
export function startGaPageviewTracking(): () => void {
  if (typeof window === 'undefined') return () => {};

  // The URL on screen, blocked routes included, so a redirect away from a
  // blocked route still reports where it came from (sanitized: no token).
  let previousUrl = INITIAL_URL;

  const track = (url: string, referrer: string) => {
    const path = pathOf(url);
    if (!path || path === lastTrackedPath) return;
    lastTrackedPath = path;

    // GA4-03 — LEAVING A PATH FORGETS THAT IT WAS MEASURED.
    //
    // `lastSentPath` exists to answer "has the screen in front of the visitor
    // already been measured", which is what tells `sendConsentedPageView`
    // whether it still owes a hit. Without this line it answers the much weaker
    // "was this path measured at any point in this document", and the make-good
    // is then suppressed for a screen the tracker itself would have re-measured:
    // accept on /property/x, clear consent from the footer, navigate to
    // /for-sale/apartments, navigate BACK to /property/x, accept again — the
    // return visit is a genuine page view under the tracker's own path dedupe,
    // and it was being dropped.
    //
    // Cleared on every path CHANGE, so a repeat of the same path (the routers
    // emit replaceState/pushState pairs) is unaffected — that returns above.
    lastSentPath = null;

    sendGaPageView(url, referrer);
  };

  // The tag may already have booted from the head on this very page, so the
  // opt-out flag has to be right before the first scroll can happen — not only
  // from the first navigation onwards.
  syncGaOptOut(window.location.pathname);

  track(INITIAL_URL, INITIAL_REFERRER);

  const onHistoryChange = () => {
    const url = window.location.href;
    const referrer = previousUrl;
    previousUrl = url;
    track(url, referrer);
  };

  // Deferred by a task so the router has finished and window.location is the
  // destination rather than the URL being left.
  //
  // GA4-13: the opt-out flag is NOT deferred with it. `pushState` has already
  // changed the address bar by the time these run, and gtag.js's own listeners
  // are live in the meantime — a scroll landing in the gap would be measured
  // against the route just entered.
  const onNavigated = (kind: GaRouteChangeKind) => {
    syncGaOptOut(window.location.pathname);
    window.setTimeout(onHistoryChange, 0);
    // GA4-02: synchronous, and below the line above only because that line
    // merely SCHEDULES the tracker. The consent banner has to learn about the
    // route change now rather than a task later — it is the transition OFF a
    // blocked route that puts the banner on screen.
    //
    // GA4-14: `kind` is passed through for the ad layer, which needs to tell a
    // navigation apart from the app rewriting the address bar under it.
    notifyGaRouteChange(kind);
  };

  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;

  window.history.pushState = function patchedPushState(this: History, ...args) {
    const result = originalPushState.apply(this, args as Parameters<History['pushState']>);
    onNavigated('push');
    return result;
  } as History['pushState'];

  window.history.replaceState = function patchedReplaceState(this: History, ...args) {
    const result = originalReplaceState.apply(this, args as Parameters<History['replaceState']>);
    onNavigated('replace');
    return result;
  } as History['replaceState'];

  const onPopState = () => onNavigated('pop');
  window.addEventListener('popstate', onPopState);

  return () => {
    window.history.pushState = originalPushState;
    window.history.replaceState = originalReplaceState;
    window.removeEventListener('popstate', onPopState);
  };
}

/**
 * GA4-02 — the page view that a grant owes the current screen.
 *
 * Pre-consent events are DROPPED, not queued: the locked decision is that
 * nothing is held in memory waiting for a yes. So the moment consent is given
 * there is exactly one hit to make good — the page the visitor is looking at
 * while they click Accept — and this is it.
 *
 * `lastTrackedPath` is written before the send. The tracker has already
 * CONSIDERED this path (it ran on landing and was refused by the gate), so it
 * holds this path already; writing it again is what makes that true in the case
 * where the tracker was never started at all, and documents that this hit is
 * the one the tracker would otherwise owe.
 *
 * The referrer is the document's original one rather than the previous in-app
 * URL. This is by construction the FIRST hit of the session — nothing was sent
 * before consent — so what GA4 needs here is where the visit came from, not
 * which of our own screens preceded the click.
 */
function sendConsentedPageView(): void {
  if (typeof window === 'undefined') return;

  const url = window.location.href;
  const path = pathOf(url);
  if (!path) return;

  // GA4-03 — THE MAKE-GOOD IS OWED ONCE, NOT ONCE PER GRANT.
  //
  // This function deliberately bypasses the tracker's `lastTrackedPath` dedupe
  // (it writes that value rather than reading it), which is correct for the
  // case it was written for — the first grant of a visit, where the page on
  // screen has never been measured. It was wrong for every grant after that,
  // and there are two reachable:
  //
  //   1. The footer's "Cookie settings" link calls `clearConsent()`, which
  //      reopens the banner on the SAME page. Accept again and the visitor's
  //      current screen was counted twice for one visit.
  //   2. A grant in another tab arrives here through the `storage` listener in
  //      utils/consent.ts, and lands on a document that may already have
  //      measured this exact path.
  //
  // Keyed on the raw path, like every other dedupe in this file. A withdrawal
  // and a fresh grant do not make the page view owed again — the screen has
  // been measured, and a second hit for it would overstate the page rather
  // than record a new consent.
  if (path === lastSentPath) return;

  lastTrackedPath = path;
  // GA4-03: the previous hit's URL when there is one, and only the document's
  // original referrer for a document that has genuinely not measured anything
  // yet. See `lastSentUrl`.
  sendGaPageView(url, lastSentUrl ?? INITIAL_REFERRER);
}

/**
 * GA4-02 — what a consent decision DOES, as opposed to what it stores.
 *
 * utils/consent.ts owns the record and publishes changes; this is the
 * subscriber that performs the GA4 half, so that a decision in the banner ends
 * up doing "bootstrap, consent update, one page view" without the two modules
 * having to import each other in a cycle.
 *
 * GA4-14 — EVERY CHANGE RE-DECLARES BOTH CATEGORIES. There are four states now,
 * not two, and three of the transitions between them leave gtag.js loaded:
 *
 *   ANALYTICS GRANTED. The route guard runs FIRST and is not negotiable:
 *   consent does not unlock a blocked route, so accepting while standing on
 *   /admin stores the decision and boots nothing. Otherwise the tag boots —
 *   `ensureGtagBootstrapped` emits `consent default` (all four denied) before
 *   `config`, then the real two-category update — and the page is measured once.
 *
 *   ANALYTICS WITHDRAWN. Unchanged from GA4-02, and the `ga-disable-` behaviour
 *   is the whole of it: gtag.js cannot be unloaded and its Enhanced Measurement
 *   listeners are not ours to remove, so the opt-out flag is the only thing that
 *   still stops them. `syncGaOptOut` re-derives it with the withdrawal in force.
 *
 *   ADVERTISING WITHDRAWN ALONE. The visitor turns advertising off in the
 *   settings panel and leaves analytics on. gtag.js STAYS LOADED and stays
 *   measuring — that is the point of a per-category panel, and it is what
 *   Art. 7(3) asks for: withdrawing one purpose must not cost you the other.
 *   All that changes is the declared state, so the next hit's `gcs` shows the
 *   three ad signals denied.
 *
 * In every case the update is built from the record as it stands, so the tag's
 * declared consent and the stored consent cannot drift apart.
 */
function applyConsentChange(snapshot: ConsentSnapshot): void {
  if (typeof window === 'undefined') return;

  // GA4-14 spoke 3c — THERE IS NOTHING FOR AN ADVERTISING CHANGE TO DO HERE.
  //
  // Spoke 3 opened this function with `syncAdsOptOut()`, on the belief that
  // `ga-disable-<id>` could stop a Google Ads destination. Production disproved
  // that (D13), and spoke 3c removed the destination itself: this tag is
  // configured for `GA_MEASUREMENT_ID` and nothing else, exactly as it was
  // after spoke 1. The advertising half of a consent change is now entirely
  // utils/adTags.ts's, which subscribes to the same notification and whose
  // whole withdrawal is "stop firing" — there is no vendor script left in the
  // document for either module to silence.
  //
  // What an advertising change still does HERE is the `consent update` below:
  // the three ad signals are re-declared on every change so the tag's declared
  // state and the stored record cannot drift. That is spoke 1's behaviour,
  // unchanged.
  if (snapshot.analytics !== 'granted') {
    syncGaOptOut(window.location.pathname);
    if (typeof window.gtag === 'function') {
      // The real payload, not a blanket denial. Analytics is off — that is why
      // we are here — but if advertising is still granted, saying otherwise
      // would under-declare a permission the visitor has actually given.
      window.gtag('consent', 'update', { ...currentConsentPayload() });
    }
    return;
  }

  if (isAnalyticsBlockedPath(window.location.pathname)) return;

  const alreadyBooted = typeof window.gtag === 'function';
  if (!ensureGtagBootstrapped()) return;

  if (alreadyBooted) {
    // Re-granting, or changing advertising, on a tag that is still loaded from
    // an earlier grant: `ensureGtagBootstrapped` short-circuits on the
    // already-booted latch, so the update and the lifting of the opt-out flag
    // have to happen here instead. This is the line that carries an
    // advertising-only change to gtag.js.
    syncGaOptOut(window.location.pathname);
    window.gtag('consent', 'update', { ...currentConsentPayload() });

    // The GA4 `config` for a document whose tag was booted without one.
    //
    // GA4-14 spoke 3 introduced a second bootstrap that loaded gtag.js for the
    // Ads destination alone, so a visitor who granted advertising first and
    // analytics second could reach this line on a booted-but-unconfigured tag.
    // Spoke 3c deleted that bootstrap — `ensureGtagBootstrapped` is the only
    // thing that boots this tag now, and it configures the property on the way
    // through — so the flag is `true` on every path that gets here today.
    //
    // Kept rather than deleted because it is the guard against the shape
    // GA4-03 fixed: a second `config` for a destination that already has one.
    // Costing nothing and refusing a double config is the right trade for a
    // line whose absence caused a double page view once already.
    if (!analyticsDestinationConfigured) {
      analyticsDestinationConfigured = true;
      window.gtag('config', GA_MEASUREMENT_ID, { ...GA_CONFIG_PARAMS, ...documentReferrerConfig() });
    }
  }

  sendConsentedPageView();
}

/**
 * Registered at module scope, and deliberately not from a React effect.
 *
 * Every surface that can change consent — the banner, the footer's "Cookie
 * settings" link — is rendered by `src/pages/+Layout.tsx`, which also imports
 * this module; and `components/GoogleAnalytics.tsx` imports it everywhere else.
 * Tying the subscription to a component's lifecycle would add an ordering
 * question ("was the tracker mounted when Accept was clicked?") that has no
 * upside, since this listener does nothing at all until somebody decides.
 *
 * Safe with no DOM: `onConsentChange` touches `window` only through a guarded
 * `storage` listener, and `applyConsentChange` returns immediately without one.
 */
onConsentChange(applyConsentChange);
